import React, { useEffect, useMemo, useState } from "react";
import ProyectoFinanzasPanel from "./ProyectoFinanzasPanel.jsx";
import PortfolioDirectionView from "./PortfolioDirectionView.jsx";

import PortfolioReviewView from "./PortfolioReviewView.jsx";
import PortfolioBubbleAnalysisView from "./PortfolioBubbleAnalysisView.jsx";
import "./ProyectoFinanzasPanel.css";
import "./ProyectosCommandCenterView.css";
import { openPortfolioExecutiveReport } from "./portfolioExecutiveReport";

const API_PROYECTOS =
  process.env.REACT_APP_API_PROYECTOS ||
  "https://g4xylhvnxf.execute-api.us-east-1.amazonaws.com/dev";

// ============================================================
// PCC_AUTH_TOKEN_GUARD_V1
// Evita enviar JWT Cognito expirados al API.
// ============================================================

function decodeJwtPayload(token) {
  try {
    const part = String(token || "").split(".")[1];

    if (!part) return null;

    const normalized = part
      .replace(/-/g, "+")
      .replace(/_/g, "/");

    const padded =
      normalized +
      "=".repeat((4 - (normalized.length % 4)) % 4);

    return JSON.parse(atob(padded));
  } catch {
    return null;
  }
}

function isJwtExpired(token, toleranceSeconds = 30) {
  const payload = decodeJwtPayload(token);

  if (!payload?.exp) return true;

  const now = Math.floor(Date.now() / 1000);

  return Number(payload.exp) <= now + toleranceSeconds;
}

function clearExpiredAuthSession() {
  localStorage.removeItem("idToken");
  localStorage.removeItem("accessToken");
}

function getAuthToken() {
  const idToken = localStorage.getItem("idToken");
  const accessToken = localStorage.getItem("accessToken");

  const token =
    idToken && !isJwtExpired(idToken)
      ? idToken
      : accessToken && !isJwtExpired(accessToken)
        ? accessToken
        : null;

  if (!token) {
    clearExpiredAuthSession();

    throw new Error(
      "La sesion expiro. Vuelve a iniciar sesion."
    );
  }

  return token;
}

async function apiRequest(path, options = {}) {
  const token = getAuthToken();

  const res = await fetch(`${API_PROYECTOS}${path}`, {
    ...options,
    headers: {
      Authorization: token,
      "Content-Type": "application/json",
      ...(options.headers || {}),
    },
  });

  const text = await res.text();
  let data = {};

  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    data = { raw: text };
  }

  if (!res.ok) {
    throw new Error(data.message || `Error HTTP ${res.status}`);
  }

  return data;
}

function normalizarPrioridad(value) {
  const raw = String(value || "Media").toUpperCase();

  if (raw === "ALTA") return "Alta";
  if (raw === "BAJA") return "Baja";
  if (raw === "CRITICA" || raw === "CRÍTICA") return "Crítica";
  return "Media";
}

function normalizarProyectoBackend(p) {
  const estado = String(p.estado || "BIEN").toUpperCase();

  let riesgo = "Bajo";
  if (estado === "ATENCION") riesgo = "Medio";
  if (estado === "RIESGO") riesgo = "Alto";
  if (estado === "BLOQUEADO") riesgo = "Crítico";

  return {
    ...p,
    id: p.id || p.proyectoId,
    proyectoId: p.proyectoId || p.id,
    nombre: p.nombre || "Proyecto sin nombre",
    cliente: p.cliente || "IP DEPOT",
    responsable: p.responsable || "Sin responsable",
    fase: p.etapa || "Planeación",
    estatus: p.estado || "BIEN",
    prioridad: normalizarPrioridad(p.prioridad),
    avance: Number(p.avance || 0),
    fechaCompromiso: p.fechaCompromiso || "",
    montoTotal: Number(p.montoContratado || 0),
    montoPagado: Number(p.montoPagado || 0),
    riesgo,
    bloqueo: p.bloqueo || "",
    siguienteAccion: p.proximoPaso || "",
    decisionRequerida: p.decision || "",
  };
}

function normalizarTareaBackend(t) {
  return {
    ...t,
    id: t.id || t.taskId,
    taskId: t.taskId || t.id,
    proyectoId: t.proyectoId,
    titulo: t.titulo || "Tarea sin título",
    responsable: t.responsable || "Sin asignar",
    estado: t.estado || "Pendiente",
    prioridad: normalizarPrioridad(t.prioridad),
    fechaLimite: t.fechaLimite || "",
    checklist: Array.isArray(t.checklist) ? t.checklist : [],
  };
}


const FASES = [
  "Idea",
  "Planeación",
  "Análisis",
  "Desarrollo",
  "Pruebas",
  "Producción",
  "Pausado",
  "Bloqueado",
  "Cerrado",
];

const PRIORIDADES = ["Crítica", "Alta", "Media", "Baja"];

const proyectosIniciales = [
  {
    id: "proy-001",
    nombre: "Estaciones de trabajo pasaportes",
    cliente: "Gobierno",
    responsable: "Dirección de Proyectos",
    fase: "Desarrollo",
    estatus: "En curso",
    prioridad: "Alta",
    avance: 68,
    fechaCompromiso: "2026-07-15",
    montoTotal: 0,
    montoPagado: 0,
    riesgo: "Medio",
    bloqueo: "",
    siguienteAccion: "Validar cotización de consumibles PVC e impresoras.",
    decisionRequerida: "Definir proveedor principal.",
  },
  {
    id: "proy-002",
    nombre: "Inventario IP DEPOT",
    cliente: "IP DEPOT",
    responsable: "Equipo interno",
    fase: "Pruebas",
    estatus: "En curso",
    prioridad: "Alta",
    avance: 78,
    fechaCompromiso: "2026-06-30",
    montoTotal: 0,
    montoPagado: 0,
    riesgo: "Bajo",
    bloqueo: "",
    siguienteAccion: "Conectar módulos aislados a DynamoDB.",
    decisionRequerida: "Priorizar módulos para producción.",
  },
  {
    id: "proy-003",
    nombre: "RENAPO Biometric Client",
    cliente: "RENAPO",
    responsable: "Arquitectura",
    fase: "Producción",
    estatus: "En curso",
    prioridad: "Crítica",
    avance: 85,
    fechaCompromiso: "2026-07-05",
    montoTotal: 0,
    montoPagado: 0,
    riesgo: "Medio",
    bloqueo: "Prueba final con hardware real.",
    siguienteAccion: "Validar instalación y firma de paquete.",
    decisionRequerida: "Aprobar release instalable.",
  },
  {
    id: "proy-004",
    nombre: "Cotizaciones Consumibles PVC",
    cliente: "IP DEPOT",
    responsable: "Compras / Proyectos",
    fase: "Análisis",
    estatus: "En análisis",
    prioridad: "Media",
    avance: 45,
    fechaCompromiso: "2026-06-25",
    montoTotal: 0,
    montoPagado: 0,
    riesgo: "Medio",
    bloqueo: "",
    siguienteAccion: "Cerrar proveedores finalistas.",
    decisionRequerida: "Validar CardCube vs Image China.",
  },
];

const tareasIniciales = [
  {
    id: "task-001",
    proyectoId: "proy-001",
    titulo: "Validar proveedor de tarjetas PVC",
    responsable: "Compras",
    estado: "En proceso",
    prioridad: "Alta",
    fechaLimite: "2026-06-20",
    checklist: ["Validar IVA", "Validar envío", "Validar tiempo de entrega"],
  },
  {
    id: "task-002",
    proyectoId: "proy-001",
    titulo: "Confirmar compatibilidad de impresoras PVC",
    responsable: "Técnico",
    estado: "Pendiente",
    prioridad: "Alta",
    fechaLimite: "2026-06-22",
    checklist: ["Modelo", "Ribbon compatible", "Refacciones"],
  },
  {
    id: "task-003",
    proyectoId: "proy-002",
    titulo: "Conectar cotizaciones a DynamoDB",
    responsable: "Backend",
    estado: "En proceso",
    prioridad: "Alta",
    fechaLimite: "2026-06-18",
    checklist: ["GET", "POST", "PUT", "DELETE"],
  },
  {
    id: "task-004",
    proyectoId: "proy-003",
    titulo: "Prueba con hardware real",
    responsable: "Campo",
    estado: "Pendiente",
    prioridad: "Crítica",
    fechaLimite: "2026-06-25",
    checklist: ["Topaz", "Scanner", "Huellas", "Firma"],
  },
];

function cx(...items) {
  return items.filter(Boolean).join(" ");
}

function riesgoClass(riesgo) {
  return String(riesgo || "").toLowerCase();
}

function prioridadClass(prioridad) {
  return String(prioridad || "").toLowerCase();
}


  

function parseProjectDate(value) {
  if (!value) return null;

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;

  return date;
}

function formatProjectDate(value) {
  const date = parseProjectDate(value);
  if (!date) return "Sin fecha";

  return date.toLocaleDateString("es-MX", {
    year: "numeric",
    month: "short",
    day: "2-digit",
  });
}

function getProjectLifeDays(project) {
  const start = parseProjectDate(project?.createdAt || project?.fechaInicio || project?.fechaAlta);
  if (!start) return null;

  const now = new Date();
  const diff = now.getTime() - start.getTime();

  if (diff < 0) return 0;

  return Math.max(0, Math.floor(diff / 86400000));
}

function getProjectLifeLabel(project) {
  const days = getProjectLifeDays(project);

  if (days === null) return "Vida no registrada";
  if (days === 0) return "Alta hoy";
  if (days === 1) return "1 dia activo";

  return days + " dias activo";
}

function getProjectLastUpdateLabel(project) {
  const date = parseProjectDate(project?.updatedAt);

  if (!date) return "Sin actualizacion";

  const now = new Date();
  const diffDays = Math.max(0, Math.floor((now.getTime() - date.getTime()) / 86400000));

  if (diffDays === 0) return "Actualizado hoy";
  if (diffDays === 1) return "Actualizado hace 1 dia";

  return "Actualizado hace " + diffDays + " dias";
}

function compactText(value, max = 72) {
  const text = String(value || "").trim();

  if (!text) return "Sin dato";
  if (text.length <= max) return text;

  return text.slice(0, max - 1).trim() + "…";
}

function getProjectHealth(project) {
  const riesgo = String(project?.riesgo || "").toLowerCase();
  const bloqueo = String(project?.bloqueo || "").trim();
  const decision = String(project?.decisionRequerida || "").trim();
  const avance = Number(project?.avance || 0);
  const vencido = isVencido(project?.fechaCompromiso);

  if (bloqueo || riesgo.includes("cr")) {
    return {
      label: "Bloqueado",
      className: "danger",
    };
  }

  if (vencido || riesgo.includes("alto")) {
    return {
      label: "Riesgo",
      className: "danger",
    };
  }

  if (decision || riesgo.includes("medio") || avance === 0) {
    return {
      label: "Atención",
      className: "warning",
    };
  }

  return {
    label: "En curso",
    className: "ok",
  };
}

function getProjectHealthLabel(project) {
  return getProjectHealth(project).label;
}

function getProjectHealthClass(project) {
  return getProjectHealth(project).className;
}

function isVencido(fecha) {
  if (!fecha) return false;
  const hoy = new Date();
  const f = new Date(`${fecha}T23:59:59`);
  return f < hoy;
}

const WIP_LIMITS_BY_PHASE = {
  Idea: 3,
  "Planeación": 3,
  "Planeación": 3,
  "Análisis": 3,
  "Análisis": 3,
  Desarrollo: 5,
  Pruebas: 4,
  "Producción": 4,
  "Producción": 4,
  Pausado: 3,
  Bloqueado: 2,
  Cerrado: 99,
};

function getWipLimit(fase) {
  return WIP_LIMITS_BY_PHASE[fase] || 4;
}

function getColumnWipClass(fase, count) {
  const limit = getWipLimit(fase);

  if (limit >= 99) return "";
  if (count > limit) return "wip-over";
  if (count === limit) return "wip-full";
  if (count >= Math.max(1, limit - 1)) return "wip-warning";

  return "";
}

function getColumnWipLabel(fase, count) {
  const limit = getWipLimit(fase);

  if (limit >= 99) return String(count);

  return count + "/" + limit;
}

function getDaysSince(value) {
  const date = parseProjectDate(value);
  if (!date) return null;

  const now = new Date();
  const diff = now.getTime() - date.getTime();

  if (diff < 0) return 0;

  return Math.max(0, Math.floor(diff / 86400000));
}

function getPhaseAgingDays(project) {
  /**
   * Versión frontend segura:
   * Si no hay historial real de cambio de fase todavía,
   * usamos updatedAt como mejor aproximación.
   * Cuando BE guarde eventos FASE_CAMBIADA, este cálculo se reemplaza por fechaCambioFase.
   */
  return getDaysSince(project?.fechaCambioFase || project?.updatedAt || project?.createdAt);
}

function getPhaseAgingLabel(project) {
  const days = getPhaseAgingDays(project);

  if (days === null) return "Fase sin edad";
  if (days === 0) return "En fase · hoy";
  if (days === 1) return "En fase · 1 día";

  return "En fase · " + days + " días";
}

function getPhaseAgingClass(project) {
  const days = getPhaseAgingDays(project);
  const fase = String(project?.fase || "");

  if (days === null) return "neutral";
  if (fase === "Bloqueado" || fase === "Pausado") return days >= 3 ? "danger" : "warning";
  if (days >= 15) return "danger";
  if (days >= 7) return "warning";

  return "ok";
}

function getSlaInfo(project) {
  const compromiso = parseProjectDate(project?.fechaCompromiso);

  if (!compromiso) {
    return {
      label: "SLA · Sin compromiso",
      className: "neutral",
      days: null,
    };
  }

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const due = new Date(compromiso.getFullYear(), compromiso.getMonth(), compromiso.getDate());
  const diffDays = Math.ceil((due.getTime() - today.getTime()) / 86400000);

  if (diffDays < 0) {
    return {
      label: "SLA · Vencido " + Math.abs(diffDays) + "d",
      className: "danger",
      days: diffDays,
    };
  }

  if (diffDays === 0) {
    return {
      label: "SLA · Vence hoy",
      className: "danger",
      days: diffDays,
    };
  }

  if (diffDays <= 7) {
    return {
      label: "SLA · " + diffDays + "d restantes",
      className: "warning",
      days: diffDays,
    };
  }

  return {
    label: "SLA · Normal",
    className: "ok",
    days: diffDays,
  };
}

function getSlaLabel(project) {
  return getSlaInfo(project).label;
}

function getSlaClass(project) {
  return getSlaInfo(project).className;
}

function inferirModoOperativo(p) {
  const accion = String(p?.siguienteAccion || "").toLowerCase();
  const decision = String(p?.decisionRequerida || "").toLowerCase();
  const bloqueo = String(p?.bloqueo || "").toLowerCase();
  const fase = String(p?.fase || "").toLowerCase();

  if (bloqueo) return "Bloqueado por tercero";
  if (decision || accion.includes("autoriz")) return "Pendiente de autorización";
  if (accion.includes("produccion") || accion.includes("producción") || fase.includes("producción")) return "Listo para producción";
  if (accion.includes("validar") || accion.includes("confirmar")) return "Requiere validación";
  if (accion.includes("seguimiento")) return "Seguimiento operativo";
  if (!accion) return "Requiere siguiente movimiento";

  return "En ejecución";
}

function inferirDependenciaPrincipal(p) {
  const texto = [
    p?.siguienteAccion,
    p?.decisionRequerida,
    p?.bloqueo,
    p?.cliente,
  ].join(" ").toLowerCase();

  if (texto.includes("jurid")) return "Jurídico / cliente";
  if (texto.includes("autoriz")) return "Autorización directiva";
  if (texto.includes("consumible") || texto.includes("proveedor")) return "Proveedor / compras";
  if (texto.includes("produccion") || texto.includes("producción")) return "Operación / liberación";
  if (texto.includes("hardware") || texto.includes("equipo")) return "Validación técnica";

  return "Operación interna";
}

function inferirEvidenciaFaltante(p) {
  const texto = [
    p?.siguienteAccion,
    p?.decisionRequerida,
    p?.bloqueo,
  ].join(" ").toLowerCase();

  if (texto.includes("autoriz")) return "Autorización formal";
  if (texto.includes("consumible") || texto.includes("proveedor")) return "Cotización / confirmación comercial";
  if (texto.includes("produccion") || texto.includes("producción")) return "Acta o evidencia de liberación";
  if (texto.includes("jurid")) return "Resolución / comunicado jurídico";
  if (texto.includes("validar") || texto.includes("confirmar")) return "Evidencia de validación";

  return "Documento de seguimiento";
}

function inferirAutomatizacionSugerida(p) {
  const modo = inferirModoOperativo(p);

  if (modo === "Pendiente de autorización") {
    return "Crear tarea de seguimiento y escalar a dirección.";
  }

  if (modo === "Listo para producción") {
    return "Crear checklist de salida a producción.";
  }

  if (modo === "Requiere validación") {
    return "Crear tarea técnica con evidencia obligatoria.";
  }

  if (modo === "Bloqueado por tercero") {
    return "Registrar bloqueo y generar evento de seguimiento.";
  }

  return "Mantener seguimiento semanal automático.";
}

// PCC_NEW_PROJECT_MODAL_V1
// PCC_GENERAL_FORM_V1_1

// ============================================================
// PCC_COMMERCIAL_CATALOG_V2
// Catalogo comercial obtenido desde backend / DynamoDB.
// No existen porcentajes comerciales hardcodeados en React.
// ============================================================

function getCommercialProbability(stage, catalog = []) {
  const key = String(stage || "").trim().toUpperCase();

  const rule = (Array.isArray(catalog) ? catalog : []).find(
    (item) =>
      String(item?.codigo || "").trim().toUpperCase() === key
  );

  const value = Number(rule?.probabilidad || 0);

  if (!Number.isFinite(value)) return 0;

  return Math.max(0, Math.min(100, value));
}

function getWeightedPipeline(amount, probability) {
  const safeAmount = Math.max(
    0,
    Number(amount || 0)
  );

  const safeProbability = Math.max(
    0,
    Math.min(
      100,
      Number(probability || 0)
    )
  );

  return safeAmount * (safeProbability / 100);
}

function formatProjectMoney(value) {
  return new Intl.NumberFormat("es-MX", {
    style: "currency",
    currency: "MXN",
    maximumFractionDigits: 2,
  }).format(
    Math.max(
      0,
      Number(value || 0)
    )
  );
}

// PCC_GENERAL_FORM_V1_2_2
// PCC_GENERAL_FORM_V1_2_3B
// PCC_GENERAL_FORM_V1_2_4A

// ============================================================
// PCC_PROJECT_MONEY_INPUT_V1
//
// Entrada monetaria robusta para:
// - 20000000
// - 20,000,000
// - $20,000,000
// - 20 000 000
//
// El estado interno SIEMPRE conserva numero plano:
// 20000000
// ============================================================

function normalizeProjectMoneyInput(value) {
  const source = String(value ?? "")
    .trim()
    .replace(/\s+/g, "")
    .replace(/\$/g, "");

  if (!source) {
    return "";
  }

  // Si hay comas y puntos:
  // 20,000,000.50 -> comas son miles.
  if (source.includes(",") && source.includes(".")) {
    const normalized = source.replace(/,/g, "");

    return /^\d*(?:\.\d{0,2})?$/.test(normalized)
      ? normalized
      : "";
  }

  // Varias comas:
  // 20,000,000 -> separadores de miles.
  if ((source.match(/,/g) || []).length > 1) {
    const normalized = source.replace(/,/g, "");

    return /^\d+(?:\.\d{0,2})?$/.test(normalized)
      ? normalized
      : "";
  }

  // Una coma con exactamente 3 digitos a la derecha:
  // 20,000 -> miles.
  if (/^\d{1,3}(?:,\d{3})+$/.test(source)) {
    return source.replace(/,/g, "");
  }

  // Una coma como decimal:
  // 1250,50 -> 1250.50
  if (/^\d+,\d{1,2}$/.test(source)) {
    return source.replace(",", ".");
  }

  // Numero simple.
  const normalized = source.replace(/[^\d.]/g, "");

  if (!/^\d*(?:\.\d{0,2})?$/.test(normalized)) {
    return "";
  }

  return normalized;
}

function projectMoneyNumber(value) {
  const normalized =
    normalizeProjectMoneyInput(value);

  const amount = Number(normalized || 0);

  return Number.isFinite(amount)
    ? Math.max(0, amount)
    : 0;
}

function formatProjectMoneyExact(value) {
  return projectMoneyNumber(value)
    .toLocaleString("es-MX", {
      style: "currency",
      currency: "MXN",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
}

function formatProjectMoneyMdp(value) {
  const amount = projectMoneyNumber(value);

  if (amount <= 0) {
    return "$0.0 mdp";
  }

  return (
    "$" +
    (amount / 1000000).toLocaleString(
      "es-MX",
      {
        minimumFractionDigits: 1,
        maximumFractionDigits: 2,
      }
    ) +
    " mdp"
  );
}

// PCC_PROJECT_MONEY_INPUT_V1_END

const PROJECT_SITUATIONS = {
  VENTA_ACTIVA: "VENTA_ACTIVA",
  PAUSADO: "VENTA_DETENIDA_TEMPORALMENTE",
  EJECUCION: "EJECUCION",
  CERRADO: "CERRADO_SATISFACTORIAMENTE",
  NO_REALIZADA: "VENTA_NO_REALIZADA",
  MUERTO: "MUERTO",
};

function normalizeProjectSituation(value) {
  const situation = String(value || "")
    .trim()
    .toUpperCase();

  // Compatibilidad con proyectos anteriores.
  if (situation === "CERRADO") {
    return PROJECT_SITUATIONS.CERRADO;
  }

  return situation || PROJECT_SITUATIONS.VENTA_ACTIVA;
}

function isCommercialProjectSituation(value) {
  const situation = normalizeProjectSituation(value);

  return (
    situation === PROJECT_SITUATIONS.VENTA_ACTIVA ||
    situation === PROJECT_SITUATIONS.PAUSADO
  );
}

function isTerminalProjectSituation(value) {
  const situation = normalizeProjectSituation(value);

  return (
    situation === PROJECT_SITUATIONS.CERRADO ||
    situation === PROJECT_SITUATIONS.NO_REALIZADA ||
    situation === PROJECT_SITUATIONS.MUERTO
  );
}

function getClosingResultForSituation(value) {
  const situation = normalizeProjectSituation(value);

  if (situation === PROJECT_SITUATIONS.CERRADO) {
    return "CERRADO";
  }

  if (situation === PROJECT_SITUATIONS.NO_REALIZADA) {
    return "PERDIDO";
  }

  if (situation === PROJECT_SITUATIONS.MUERTO) {
    return "MUERTO";
  }

  return "";
}

function getProjectSituationLabel(value) {
  const situation = normalizeProjectSituation(value);

  if (situation === PROJECT_SITUATIONS.VENTA_ACTIVA) {
    return "Venta activa";
  }

  if (situation === PROJECT_SITUATIONS.PAUSADO) {
    return "Venta detenida temporalmente";
  }

  if (situation === PROJECT_SITUATIONS.EJECUCION) {
    return "En ejecución";
  }

  if (situation === PROJECT_SITUATIONS.CERRADO) {
    return "Cerrado satisfactoriamente";
  }

  if (situation === PROJECT_SITUATIONS.NO_REALIZADA) {
    return "Venta no realizada";
  }

  if (situation === PROJECT_SITUATIONS.MUERTO) {
    return "Muerto / Sin continuidad";
  }

  return "Sin situación";
}

function getProjectStepThreeTitle(value) {
  const situation = normalizeProjectSituation(value);

  if (situation === PROJECT_SITUATIONS.VENTA_ACTIVA) {
    return "Información comercial";
  }

  if (situation === PROJECT_SITUATIONS.PAUSADO) {
    return "Información comercial en pausa";
  }

  if (situation === PROJECT_SITUATIONS.EJECUCION) {
    return "Información de ejecución";
  }

  if (situation === PROJECT_SITUATIONS.CERRADO) {
    return "Cierre satisfactorio";
  }

  if (situation === PROJECT_SITUATIONS.NO_REALIZADA) {
    return "Venta no realizada";
  }

  return "Cierre sin continuidad";
}

function getProjectFormValidation(form) {
  const missing = [];

  const nombre =
    String(form?.nombre || "").trim();

  const responsable =
    String(form?.responsable || "").trim();

  const siguienteAccion =
    String(form?.proximoPaso || "").trim();

  const situacion =
    normalizeProjectSituation(
      form?.situacionProyecto
    );

  if (!nombre) {
    missing.push("Nombre del proyecto");
  }

  if (!responsable) {
    missing.push("Responsable");
  }

  if (
    !isTerminalProjectSituation(situacion) &&
    !siguienteAccion
  ) {
    missing.push("Siguiente acción");
  }

  if (isCommercialProjectSituation(situacion)) {

    if (!form?.etapaComercial) {
      missing.push("Etapa comercial");
    }

    if (
      projectMoneyNumber(form?.montoOportunidad) <= 0
    ) {
      missing.push("Valor estimado del proyecto");
    }
  }

  if (situacion === PROJECT_SITUATIONS.EJECUCION) {

    if (!form?.etapa) {
      missing.push("Fase operativa");
    }

    if (
      projectMoneyNumber(form?.montoContratado) <= 0
    ) {
      missing.push("Monto contratado");
    }
  }

  if (isTerminalProjectSituation(situacion)) {

    if (!form?.fechaCierre) {
      missing.push("Fecha de cierre");
    }

    if (
      !String(form?.motivoCierre || "").trim()
    ) {
      missing.push("Motivo de cierre");
    }
  }

  return {
    valid: missing.length === 0,
    missing,
  };
}

function getSituationGuidance(value) {
  const situation =
    normalizeProjectSituation(value);

  if (situation === PROJECT_SITUATIONS.VENTA_ACTIVA) {
    return {
      title:
        "La oportunidad continúa activa en el proceso comercial.",
      text:
        "Selecciona la etapa comercial real mientras todavía intentamos ganar el proyecto.",
    };
  }

  if (situation === PROJECT_SITUATIONS.PAUSADO) {
    return {
      title:
        "La oportunidad sigue vigente, pero está temporalmente detenida.",
      text:
        "Conservaremos su etapa y valor comercial para poder reactivarla posteriormente.",
    };
  }

  if (situation === PROJECT_SITUATIONS.EJECUCION) {
    return {
      title:
        "Selecciona En ejecución sólo si el proyecto ya fue ganado.",
      text:
        "Debe existir contrato, adjudicación, orden de compra o autorización formal.",
    };
  }

  if (situation === PROJECT_SITUATIONS.CERRADO) {
    return {
      title:
        "El proyecto terminó satisfactoriamente.",
      text:
        "Registra la fecha y una breve descripción del cierre.",
    };
  }

  if (situation === PROJECT_SITUATIONS.NO_REALIZADA) {
    return {
      title:
        "La oportunidad comercial concluyó sin concretarse.",
      text:
        "Registra la fecha y el motivo por el que no se realizó la venta.",
    };
  }

  return {
    title:
      "El proyecto ya no tendrá continuidad.",
    text:
      "Utiliza Muerto cuando la iniciativa fue cancelada, descartada o dejó de ser viable.",
  };
}

function getProjectWizardStepState(form, step) {
  const situacion =
    normalizeProjectSituation(
      form?.situacionProyecto
    );

  if (step === 1) {

    const missing = [];

    if (!String(form?.nombre || "").trim()) {
      missing.push("Nombre del proyecto");
    }

    if (!String(form?.responsable || "").trim()) {
      missing.push("Responsable");
    }

    return {
      valid: missing.length === 0,
      missing,
    };
  }

  if (step === 2) {

    return {
      valid: Boolean(situacion),
      missing: situacion
        ? []
        : ["Situación del proyecto"],
    };
  }

  if (step === 3) {

    const missing = [];

    if (
      !isTerminalProjectSituation(situacion) &&
      !String(form?.proximoPaso || "").trim()
    ) {
      missing.push("Siguiente acción");
    }

    if (isCommercialProjectSituation(situacion)) {

      if (!form?.etapaComercial) {
        missing.push("Etapa comercial");
      }

      if (
        projectMoneyNumber(form?.montoOportunidad) <= 0
      ) {
        missing.push("Valor estimado del proyecto");
      }
    }

    if (situacion === PROJECT_SITUATIONS.EJECUCION) {

      if (!form?.etapa) {
        missing.push("Fase operativa");
      }

      if (
        projectMoneyNumber(form?.montoContratado) <= 0
      ) {
        missing.push("Monto contratado");
      }
    }

    if (isTerminalProjectSituation(situacion)) {

      if (!form?.fechaCierre) {
        missing.push("Fecha de cierre");
      }

      if (
        !String(form?.motivoCierre || "").trim()
      ) {
        missing.push("Motivo de cierre");
      }
    }

    return {
      valid: missing.length === 0,
      missing,
    };
  }

  return getProjectFormValidation(form);
}
const EMPTY_NEW_PROJECT = {
  nombre: "",
  cliente: "IP DEPOT",
  responsable: "",
  etapa: "Idea",
  estado: "BIEN",
  prioridad: "MEDIA",
  avance: 0,
  fechaCompromiso: "",
  proximoPaso: "",
  bloqueo: "",
  decision: "",
  montoContratado: 0,
  montoPagado: 0,

  situacionProyecto: "VENTA_ACTIVA",
  tipoProyecto: "EXTERNO",

  etapaComercial: "OPORTUNIDAD",
  probabilidadComercial: 10,
  montoOportunidad: 0,

  resultadoCierre: "",
  fechaCierre: "",
  motivoCierre: "",
};

export default function ProyectosCommandCenterView() {
  const [proyectos, setProyectos] = useState([]);
  const [tareas, setTareas] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [dragProyectoId, setDragProyectoId] = useState(null);
  const [dragOverFase, setDragOverFase] = useState("");
  const [vista, setVista] = useState("kanban");
  const [modoCentro, setModoCentro] = useState("direccion");

  const [editReturnPending, setEditReturnPending] =
    useState(false);

  // FRONT020C_RETURN_PROJECT
  const [returnFinanceProjectId, setReturnFinanceProjectId] =
    useState(null);

  const [filtro, setFiltro] = useState("");
  const [loading, setLoading] = useState(false);
  const [syncError, setSyncError] = useState("");
  const [eventos, setEventos] = useState([]);
  const [panelTab, setPanelTab] = useState("resumen");

  // PCC_DIRECT_FINANCE_WORKSPACE_V1
  const [financeWorkspaceRequest, setFinanceWorkspaceRequest] =
    useState(null);
  const [executiveReportLoading, setExecutiveReportLoading] = useState(false);

  const [projectModal, setProjectModal] = useState(false);




  // FRONT020D_EDIT_CLOSE_RETURN
  useEffect(() => {
    if (!editReturnPending) return;
    if (projectModal) return;

    setModoCentro("direccion");
    setEditReturnPending(false);
  }, [editReturnPending, projectModal]);

  const [projectEditId, setProjectEditId] = useState(null);
  const [projectForm, setProjectForm] = useState(EMPTY_NEW_PROJECT);
  const [projectWizardStep, setProjectWizardStep] = useState(1);
  const [projectCreateLoading, setProjectCreateLoading] = useState(false);
  const [projectCreateError, setProjectCreateError] = useState("");
  const [commercialStages, setCommercialStages] = useState([]);
  const [commercialStagesError, setCommercialStagesError] = useState("");


  async function cargarEtapasComerciales() {
    try {
      setCommercialStagesError("");

      const data = await apiRequest("/config/commercial-stages");

      const items = Array.isArray(data?.items)
        ? data.items
            .filter((item) => item?.activo !== false)
            .sort(
              (a, b) =>
                Number(a?.orden || 0) -
                Number(b?.orden || 0)
            )
        : [];

      if (!items.length) {
        throw new Error("El catalogo comercial no contiene etapas activas.");
      }

      setCommercialStages(items);

      setProjectForm((current) => {
        const currentCode = String(
          current?.etapaComercial || ""
        ).toUpperCase();

        const selected =
          items.find(
            (item) =>
              String(item?.codigo || "").toUpperCase() ===
              currentCode
          ) || items[0];

        return {
          ...current,
          etapaComercial: selected?.codigo || "",
          probabilidadComercial: Number(
            selected?.probabilidad || 0
          ),
        };
      });
    } catch (error) {
      console.error("Error cargando catalogo comercial", error);
      setCommercialStages([]);
      setCommercialStagesError(
        error.message ||
          "No se pudo cargar el catalogo comercial."
      );
    }
  }
  function abrirModalNuevoProyecto() {
    setProjectWizardStep(1);
    setProjectForm({ ...EMPTY_NEW_PROJECT });
    setProjectCreateError("");
    setProjectModal(true);
  }

  function cerrarModalNuevoProyecto() {
    setProjectWizardStep(1);
    if (projectCreateLoading) return;

    setProjectModal(false);
    setProjectCreateError("");
    setProjectForm({ ...EMPTY_NEW_PROJECT });
  }

  async function crearProyecto(event) {
    event.preventDefault();

    if (projectCreateLoading) return;

    const nombre = String(projectForm.nombre || "").trim();
    const responsable = String(projectForm.responsable || "").trim();
    const cliente = String(projectForm.cliente || "IP DEPOT").trim();

    if (!nombre) {
      setProjectCreateError("Captura el nombre del proyecto.");
      return;
    }

    if (!responsable) {
      setProjectCreateError("Captura el responsable del proyecto.");
      return;
    }

    const formValidation =
      getProjectFormValidation(projectForm);

    if (!formValidation.valid) {
      setProjectCreateError(
        "Completa antes de crear: " +
          formValidation.missing.join(", ") +
          "."
      );
      return;
    }

    const situacionProyecto =
      projectForm.situacionProyecto || "VENTA_ACTIVA";

    const etapaComercial =
      projectForm.etapaComercial || "OPORTUNIDAD";

    const probabilidadComercial =
      normalizeProjectSituation(situacionProyecto) ===
      PROJECT_SITUATIONS.VENTA_ACTIVA
        ? getCommercialProbability(
            etapaComercial,
            commercialStages
          )
        : 0;

    const montoOportunidad =
      isCommercialProjectSituation(situacionProyecto)
        ? Math.max(
            0,
            projectMoneyNumber(
              projectForm.montoOportunidad
            )
          )
        : 0;

    const montoContratado =
      (
        normalizeProjectSituation(situacionProyecto) ===
          PROJECT_SITUATIONS.EJECUCION ||
        normalizeProjectSituation(situacionProyecto) ===
          PROJECT_SITUATIONS.CERRADO
      )
        ? Math.max(
            0,
            projectMoneyNumber(
              projectForm.montoContratado
            )
          )
        : 0;

    const normalizedSituation =
      normalizeProjectSituation(situacionProyecto);

    const faseOperativa =
      isTerminalProjectSituation(normalizedSituation)
        ? "Cerrado"
        : isCommercialProjectSituation(normalizedSituation)
          ? "Idea"
          : projectForm.etapa || "Planeacion";

    const nuevoProyecto = {
      ...projectForm,
      id: projectEditId || `PRY-${Date.now()}`,
      nombre,
      cliente: cliente || "IP DEPOT",
      responsable,
      etapa: faseOperativa,
      estado: "BIEN",
      prioridad: projectForm.prioridad || "MEDIA",
      avance:
        projectEditId
          ? Number(projectForm.avance || 0)
          : 0,

      situacionProyecto,
      tipoProyecto:
        projectForm.tipoProyecto || "EXTERNO",

      etapaComercial:
        isCommercialProjectSituation(situacionProyecto)
          ? etapaComercial
          : normalizeProjectSituation(situacionProyecto) ===
              PROJECT_SITUATIONS.EJECUCION
            ? "GANADO"
            : "",

      probabilidadComercial,
      montoOportunidad,

      montoContratado,
      montoPagado:
        projectEditId
          ? Number(projectForm.montoPagado || 0)
          : 0,

      resultadoCierre:
        isTerminalProjectSituation(situacionProyecto)
          ? getClosingResultForSituation(
              situacionProyecto
            )
          : "",

      fechaCierre:
        isTerminalProjectSituation(situacionProyecto)
          ? projectForm.fechaCierre || ""
          : "",

      motivoCierre:
        isTerminalProjectSituation(situacionProyecto)
          ? String(
              projectForm.motivoCierre || ""
            ).trim()
          : "",
    };

    setProjectCreateLoading(true);
    setProjectCreateError("");

    try {
      await apiRequest(
        projectEditId
          ? "/proyectos/" +
            encodeURIComponent(projectEditId)
          : "/proyectos",
        {
          method:
            projectEditId
              ? "PUT"
              : "POST",
          body: JSON.stringify(nuevoProyecto),
        }
      );

      await cargarProyectos();

      setSelectedId(nuevoProyecto.id);
      setPanelTab("resumen");
      setProjectForm({ ...EMPTY_NEW_PROJECT });
      setProjectEditId(null);
      setProjectModal(false);
    } catch (error) {
      console.error("Error creando proyecto", error);

      setProjectCreateError(
        error.message ||
          "No se pudo guardar el proyecto en DynamoDB."
      );
    } finally {
      setProjectCreateLoading(false);
    }
  }

  async function cargarProyectos() {
    setLoading(true);
    setSyncError("");

    try {
      const data = await apiRequest("/proyectos");
      const items = Array.isArray(data.items)
        ? data.items.map(normalizarProyectoBackend)
        : [];

      setProyectos(items);

      if (items.length > 0) {
        setSelectedId((current) => current || items[0].id);
      } else {
        setSelectedId(null);
      }
    } catch (error) {
      console.error("Error cargando proyectos", error);
      setSyncError(error.message || "No se pudieron cargar los proyectos.");
    } finally {
      setLoading(false);
    }
  }

  async function cargarTareasProyecto(proyectoId) {
    if (!proyectoId) {
      setTareas([]);
      return;
    }

    try {
      const data = await apiRequest(`/proyectos/${proyectoId}/tareas`);
      const items = Array.isArray(data.items)
        ? data.items.map(normalizarTareaBackend)
        : [];

      setTareas((prev) => {
        const otras = prev.filter((t) => t.proyectoId !== proyectoId);
        return [...otras, ...items];
      });
    } catch (error) {
      console.error("Error cargando tareas", error);
      setSyncError(error.message || "No se pudieron cargar las tareas.");
    }
  }


  async function cargarEventosProyecto(proyectoId) {
    if (!proyectoId) {
      setEventos([]);
      return;
    }

    try {
      const data = await apiRequest(`/proyectos/${proyectoId}/eventos`);

      const items = Array.isArray(data.items)
        ? data.items
        : Array.isArray(data.eventos)
          ? data.eventos
          : [];

      const ordenados = [...items].sort((a, b) =>
        String(b.createdAt || b.fecha || "").localeCompare(String(a.createdAt || a.fecha || ""))
      );

      setEventos(ordenados);
    } catch (error) {
      console.error("Error cargando bitácora", error);
      setEventos([]);
    }
  }

  function usuarioVisibleEvento(ev) {
    const usuario =
      ev.usuarioEmail ||
      ev.email ||
      ev.usuarioNombre ||
      ev.usuarioName ||
      ev.usuario ||
      "";

    const esUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(String(usuario));

    if (!usuario || esUuid) return "Usuario autenticado";
    return usuario;
  }

  function fechaVisibleEvento(ev) {
    const valor = ev.createdAt || ev.fecha || ev.updatedAt;
    if (!valor) return "Sin fecha";

    try {
      return new Date(valor).toLocaleString("es-MX", {
        dateStyle: "short",
        timeStyle: "short",
      });
    } catch {
      return valor;
    }
  }

  function tipoVisibleEvento(tipo) {
    const mapa = {
      PROYECTO_CREADO: "Proyecto creado",
      PROYECTO_ACTUALIZADO: "Proyecto actualizado",
      TASK_CREATED: "Tarea creada",
      TASK_UPDATED: "Tarea actualizada",
      TASK_DELETED: "Tarea eliminada",
      CHECKLIST_UPDATED: "Checklist actualizado",
    };

    return mapa[tipo] || tipo || "Movimiento";
  }

  useEffect(() => {
    cargarProyectos();
    cargarEtapasComerciales();
  }, []);


  async function cargarEventosProyecto(proyectoId) {
    if (!proyectoId) {
      setEventos([]);
      return;
    }

    try {
      const data = await apiRequest(`/proyectos/${proyectoId}/eventos`);

      const items = Array.isArray(data.items)
        ? data.items
        : Array.isArray(data.eventos)
          ? data.eventos
          : [];

      const ordenados = [...items].sort((a, b) =>
        String(b.createdAt || b.fecha || "").localeCompare(String(a.createdAt || a.fecha || ""))
      );

      setEventos(ordenados);
    } catch (error) {
      console.error("Error cargando bitácora", error);
      setEventos([]);
    }
  }

  function usuarioVisibleEvento(ev) {
    const usuario =
      ev.usuarioEmail ||
      ev.email ||
      ev.usuarioNombre ||
      ev.usuarioName ||
      ev.usuario ||
      "";

    const esUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(String(usuario));

    if (!usuario || esUuid) return "Usuario autenticado";
    return usuario;
  }

  function fechaVisibleEvento(ev) {
    const valor = ev.createdAt || ev.fecha || ev.updatedAt;
    if (!valor) return "Sin fecha";

    try {
      return new Date(valor).toLocaleString("es-MX", {
        dateStyle: "short",
        timeStyle: "short",
      });
    } catch {
      return valor;
    }
  }

  function tipoVisibleEvento(tipo) {
    const mapa = {
      PROYECTO_CREADO: "Proyecto creado",
      PROYECTO_ACTUALIZADO: "Proyecto actualizado",
      TASK_CREATED: "Tarea creada",
      TASK_UPDATED: "Tarea actualizada",
      TASK_DELETED: "Tarea eliminada",
      CHECKLIST_UPDATED: "Checklist actualizado",
    };

    return mapa[tipo] || tipo || "Movimiento";
  }

  useEffect(() => {
    if (selectedId) {
      cargarTareasProyecto(selectedId);
    }
  }, [selectedId]);

  function abrirModalEditarProyecto(project) {
    if (!project) return;

    const id = String(
      project.id ||
      project.proyectoId ||
      ""
    );

    if (!id) return;

    const situacion =
      normalizeProjectSituation(
        project.situacionProyecto
      );

    setProjectEditId(id);

    setProjectForm({
      ...EMPTY_NEW_PROJECT,
      ...project,

      id,

      nombre: project.nombre || "",
      cliente: project.cliente || "IP DEPOT",
      responsable: project.responsable || "",

      etapa:
        project.etapa ||
        project.fase ||
        "",

      estado:
        project.estado ||
        project.estatus ||
        "BIEN",

      prioridad:
        project.prioridad ||
        "MEDIA",

      avance:
        Number(project.avance || 0),

      fechaCompromiso:
        project.fechaCompromiso || "",

      proximoPaso:
        project.proximoPaso ||
        project.siguienteAccion ||
        "",

      bloqueo:
        project.bloqueo || "",

      decision:
        project.decision ||
        project.decisionRequerida ||
        "",

      montoOportunidad:
        Number(project.montoOportunidad || 0),

      montoContratado:
        Number(
          project.montoContratado ??
          project.montoContrato ??
          0
        ),

      montoPagado:
        Number(
          project.montoPagado ??
          project.montoCobrado ??
          0
        ),

      situacionProyecto:
        situacion,

      tipoProyecto:
        project.tipoProyecto || "EXTERNO",

      etapaComercial:
        project.etapaComercial ||
        project.reglaComercialAplicada?.codigo ||
        "",

      probabilidadComercial:
        Number(
          project.probabilidadComercial ??
          project.reglaComercialAplicada?.probabilidad ??
          0
        ),

      resultadoCierre:
        project.resultadoCierre ||
        getClosingResultForSituation(
          situacion
        ),

      fechaCierre:
        project.fechaCierre || "",

      motivoCierre:
        project.motivoCierre || "",
    });

    setProjectCreateError("");
    setProjectWizardStep(1);
    setSelectedId(id);
    setProjectModal(true);
  }

  function seleccionarProyectoFuerte(projectId) {
    const id = String(projectId || "");
    const proyecto = proyectos.find((p) => String(p.id) === id || String(p.proyectoId) === id);

    console.log("[CommandCenter] seleccionarProyectoFuerte", {
      id,
      encontrado: proyecto?.nombre,
      proyectoId: proyecto?.id || proyecto?.proyectoId,
    });

    if (!proyecto) return;

    localStorage.setItem("commandCenterProyectoId", proyecto.id || proyecto.proyectoId);
    localStorage.setItem("commandCenterProyectoNombre", proyecto.nombre || "");
    setSelectedId(proyecto.id || proyecto.proyectoId);
    setPanelTab("resumen");
  }

  function seleccionarProyectoPorId(projectId) {
    const id = String(projectId || "");
    const proyecto = proyectos.find(
      (p) => String(p.id) === id || String(p.proyectoId) === id
    );

    if (!proyecto) {
      setSelectedId(projectId || null);
      return;
    }

    localStorage.setItem("commandCenterProyectoId", proyecto.id || proyecto.proyectoId);
    localStorage.setItem("commandCenterProyectoNombre", proyecto.nombre || "");
    setSelectedId(proyecto.id || proyecto.proyectoId);
  }

  const proyectoActual = useMemo(() => {
    if (!proyectos.length) return null;

    if (selectedId) {
      const found = proyectos.find(
        (p) => String(p.id) === String(selectedId) || String(p.proyectoId) === String(selectedId)
      );

      if (found) return found;
    }

    return proyectos[0] || null;
  }, [proyectos, selectedId]);

  async function moverProyectoPorDrag(projectId, nuevaFase) {
    const id = String(projectId || "");
    const faseDestino = String(nuevaFase || "");

    if (!id || !faseDestino) return;

    const proyecto = proyectos.find(
      (p) => String(p.id) === id || String(p.proyectoId) === id
    );

    if (!proyecto) return;
    if (proyecto.fase === faseDestino) return;

    // SAFE_FASE_CAMBIADA_EVENT_V2
    const faseAnteriorEvento = proyecto.fase || "";
    const descripcionCambioFaseEvento =
      "Cambio de fase: " +
      faseAnteriorEvento +
      " -> " +
      faseDestino +
      " - " +
      (proyecto.nombre || "Proyecto");


    const prevProyectos = proyectos;
    const actualizadoLocal = {
      ...proyecto,
      fase: faseDestino,
      etapa: faseDestino,
    };

    setProyectos((prev) =>
      prev.map((p) =>
        String(p.id) === id || String(p.proyectoId) === id
          ? actualizadoLocal
          : p
      )
    );

    seleccionarProyectoPorId(id);

    try {
      const resp = await apiRequest("/proyectos/" + encodeURIComponent(id), {
        method: "PUT",
        body: JSON.stringify({
          ...proyecto,
          fase: faseDestino,
          etapa: faseDestino,
          estado: proyecto.estatus || proyecto.estado || "BIEN",
          prioridad: proyecto.prioridad || "Media",
          avance: Number(proyecto.avance || 0),
          montoContratado: Number(proyecto.montoTotal || proyecto.montoContratado || 0),
          montoPagado: Number(proyecto.montoPagado || 0),
          proximoPaso: proyecto.siguienteAccion || proyecto.proximoPaso || "",
          decision: proyecto.decisionRequerida || proyecto.decision || "",
        }),
      });

      try {
        await registrarEventoProyecto(
          id,
          "FASE_CAMBIADA",
          descripcionCambioFaseEvento
        );

        setSelectedId(id);
        setPanelTab("bitacora");
      } catch (eventError) {
        console.warn("No se pudo registrar FASE_CAMBIADA, pero el movimiento se conserva.", eventError);
      }


      const item = resp?.item || resp?.proyecto || resp;
      const normalizado = item?.id || item?.proyectoId
        ? normalizarProyectoBackend(item)
        : actualizadoLocal;

      setProyectos((prev) =>
        prev.map((p) =>
          String(p.id) === id || String(p.proyectoId) === id
            ? { ...p, ...normalizado, fase: normalizado.fase || faseDestino }
            : p
        )
      );

      seleccionarProyectoPorId(id);
    } catch (error) {
      console.error("Error moviendo proyecto por drag", error);
      setProyectos(prevProyectos);
      alert("No se pudo mover el proyecto. Se restauró la fase anterior.");
    } finally {
      setDragProyectoId(null);
      setDragOverFase("");
    }
  }

  function onDragStartProyecto(event, projectId) {
    const id = String(projectId || "");
    setDragProyectoId(id);

    try {
      event.dataTransfer.effectAllowed = "move";
      event.dataTransfer.setData("text/plain", id);
    } catch {
      // noop
    }
  }

  function onDragOverFase(event, fase) {
    event.preventDefault();
    setDragOverFase(fase);
  }

  function onDropFase(event, fase) {
    event.preventDefault();

    let projectId = dragProyectoId;

    try {
      projectId = event.dataTransfer.getData("text/plain") || projectId;
    } catch {
      // noop
    }

    moverProyectoPorDrag(projectId, fase);
  }

  function onDragEndProyecto() {
    setDragProyectoId(null);
    setDragOverFase("");
  }

  async function handleExecutiveReportPdf() {
    try {
      setExecutiveReportLoading(true);

      await openPortfolioExecutiveReport({
        apiRequest,
        proyectos,
      });
    } catch (error) {
      console.error("Error generando reporte directivo", error);
      alert("No se pudo generar el reporte directivo.");
    } finally {
      setExecutiveReportLoading(false);
    }
  }

  const tareasProyecto = useMemo(() => {
    if (!proyectoActual) return [];
    return tareas.filter((t) => t.proyectoId === proyectoActual.id);
  }, [tareas, proyectoActual]);

  const metricas = useMemo(() => {
    const activos = proyectos.filter((p) => !["Cerrado"].includes(p.fase)).length;
    const bloqueados = proyectos.filter((p) => p.fase === "Bloqueado" || p.bloqueo).length;
    const vencidos = proyectos.filter((p) => isVencido(p.fechaCompromiso) && p.fase !== "Cerrado").length;
    const decisiones = proyectos.filter((p) => p.decisionRequerida).length;
    const avancePromedio = proyectos.length
      ? Math.round(proyectos.reduce((acc, p) => acc + Number(p.avance || 0), 0) / proyectos.length)
      : 0;

    return {
      activos,
      bloqueados,
      vencidos,
      decisiones,
      avancePromedio,
    };
  }, [proyectos]);

  const proyectosFiltrados = useMemo(() => {
    const q = filtro.trim().toLowerCase();
    if (!q) return proyectos;

    return proyectos.filter((p) =>
      [p.nombre, p.cliente, p.responsable, p.fase, p.estatus, p.siguienteAccion]
        .join(" ")
        .toLowerCase()
        .includes(q)
    );
  }, [proyectos, filtro]);

  async function moverProyecto(id, nuevaFase) {
    const actual = proyectos.find((p) => p.id === id);
    if (!actual) return;

    const estadoNuevo =
      nuevaFase === "Bloqueado"
        ? "BLOQUEADO"
        : nuevaFase === "Cerrado"
          ? "BIEN"
          : actual.estado || "BIEN";

    try {
      setLoading(true);

      await apiRequest(`/proyectos/${id}`, {
        method: "PUT",
        body: JSON.stringify({
          ...actual,
          etapa: nuevaFase,
          estado: estadoNuevo,
        }),
      });

      await cargarProyectos();
    } catch (error) {
      console.error("Error moviendo proyecto", error);
      alert("No se pudo mover el proyecto.");
    } finally {
      setLoading(false);
    }
  }


  async function registrarEventoProyecto(proyectoId, tipo, descripcion) {
    if (!proyectoId || !tipo || !descripcion) return;

    try {
      await apiRequest(`/proyectos/${proyectoId}/eventos`, {
        method: "POST",
        body: JSON.stringify({
          tipo,
          descripcion,
        }),
      });

      await cargarEventosProyecto(proyectoId);
    } catch (error) {
      console.warn("No se pudo registrar evento de bitácora", error);
    }
  }

  async function cambiarEstadoTarea(id, estado) {
    const tarea = tareas.find((t) => t.id === id);
    if (!tarea) return;

    try {
      setLoading(true);

      await apiRequest(`/proyectos/${tarea.proyectoId}/tareas/${tarea.taskId || tarea.id}`, {
        method: "PUT",
        body: JSON.stringify({
          ...tarea,
          estado,
        }),
      });

      await registrarEventoProyecto(
        tarea.proyectoId,
        "TASK_UPDATED",
        `Se actualizó la tarea "${tarea.titulo}" a estado "${estado}".`
      );

      await cargarTareasProyecto(tarea.proyectoId);
    } catch (error) {
      console.error("Error actualizando tarea", error);
      alert("No se pudo actualizar la tarea.");
    } finally {
      setLoading(false);
    }
  }

  function detenerEventoCard(event) {
    if (!event) return;

    event.preventDefault();
    event.stopPropagation();
  }

  function scrollInspectorProyecto() {
    window.setTimeout(() => {
      const detail = document.querySelector(".pcc-detail");
      if (detail) {
        detail.scrollIntoView({
          behavior: "smooth",
          block: "start",
          inline: "nearest",
        });
      }
    }, 80);
  }

  function abrirTabProyectoDesdeCard(event, project, tab) {
    detenerEventoCard(event);

    if (!project?.id) return;

    seleccionarProyectoPorId(project.id);
    setPanelTab(tab);

    if (tab === "bitacora") {
      cargarEventosProyecto(project.id);
    }

    if (tab === "tareas") {
      cargarTareasProyecto(project.id);
    }

    scrollInspectorProyecto();
  }

  async function agregarTareaRapidaProyecto(project) {
    if (!project?.id) return;

    seleccionarProyectoPorId(project.id);
    setPanelTab("tareas");

    const titulo = window.prompt("Nueva tarea para: " + (project.nombre || "este proyecto"));
    if (!titulo) return;

    try {
      setLoading(true);

      await apiRequest("/proyectos/" + encodeURIComponent(project.id) + "/tareas", {
        method: "POST",
        body: JSON.stringify({
          titulo,
          responsable: project.responsable || "Sin asignar",
          estado: "Pendiente",
          prioridad: project.prioridad || "Media",
          fechaLimite: project.fechaCompromiso || "",
          checklist: [],
        }),
      });

      await cargarTareasProyecto(project.id);
    } catch (error) {
      console.error("Error creando tarea rápida desde card", error);
      alert("No se pudo crear la tarea.");
    } finally {
      setLoading(false);
    }
  }

  async function agregarTareaRapida() {
    if (!proyectoActual) return;

    const titulo = window.prompt("Nueva tarea para este proyecto:");
    if (!titulo) return;

    try {
      setLoading(true);

      await apiRequest(`/proyectos/${proyectoActual.id}/tareas`, {
        method: "POST",
        body: JSON.stringify({
          titulo,
          responsable: "Sin asignar",
          estado: "Pendiente",
          prioridad: "Media",
          checklist: [],
        }),
      });

      await cargarTareasProyecto(proyectoActual.id);
    } catch (error) {
      console.error("Error creando tarea", error);
      alert("No se pudo crear la tarea.");
    } finally {
      setLoading(false);
    }
  }


  async function crearTareaSeguimientoOperativo() {
    if (!proyectoActual) return;

    const modo = inferirModoOperativo(proyectoActual);
    const dependencia = inferirDependenciaPrincipal(proyectoActual);
    const evidencia = inferirEvidenciaFaltante(proyectoActual);

    try {
      setLoading(true);

      await apiRequest(`/proyectos/${proyectoActual.id}/tareas`, {
        method: "POST",
        body: JSON.stringify({
          titulo: `Seguimiento operativo: ${modo}`,
          descripcion: `Dependencia principal: ${dependencia}. Evidencia faltante: ${evidencia}. Proyecto: ${proyectoActual.nombre}.`,
          responsable: proyectoActual.responsable || "Sin asignar",
          prioridad: proyectoActual.prioridad || "Media",
          estado: "Pendiente",
          fechaLimite: proyectoActual.fechaCompromiso || "",
          checklist: [
            `Confirmar dependencia: ${dependencia}`,
            `Obtener evidencia: ${evidencia}`,
            "Actualizar siguiente movimiento",
            "Registrar avance o bloqueo"
          ],
          tags: ["seguimiento", "work-os", "proyectos"]
        }),
      });

      await cargarTareasProyecto(proyectoActual.id);
    } catch (error) {
      console.error("Error creando tarea de seguimiento", error);
      alert("No se pudo crear la tarea de seguimiento.");
    } finally {
      setLoading(false);
    }
  }

  async function solicitarAutorizacionOperativa() {
    if (!proyectoActual) return;

    try {
      setLoading(true);

      await apiRequest(`/proyectos/${proyectoActual.id}/tareas`, {
        method: "POST",
        body: JSON.stringify({
          titulo: "Solicitar autorización formal",
          descripcion: `Preparar solicitud de autorización para avanzar el proyecto: ${proyectoActual.nombre}.`,
          responsable: proyectoActual.responsable || "Sin asignar",
          prioridad: "Alta",
          estado: "Pendiente",
          fechaLimite: proyectoActual.fechaCompromiso || "",
          checklist: [
            "Preparar justificación",
            "Adjuntar evidencia técnica",
            "Solicitar autorización",
            "Registrar respuesta"
          ],
          tags: ["autorizacion", "direccion", "proyectos"]
        }),
      });

      await cargarTareasProyecto(proyectoActual.id);
    } catch (error) {
      console.error("Error solicitando autorización", error);
      alert("No se pudo crear la tarea de autorización.");
    } finally {
      setLoading(false);
    }
  }


  function calcularAvanceChecklist(checklist = []) {
    if (!Array.isArray(checklist) || checklist.length === 0) return 0;

    const completados = checklist.filter((item) => Boolean(item.completado)).length;
    return Math.round((completados / checklist.length) * 100);
  }

  async function toggleChecklistItem(tareaId, checklistId) {
    const tarea = tareas.find((t) => t.id === tareaId || t.taskId === tareaId);
    if (!tarea) return;

    const checklistActual = Array.isArray(tarea.checklist) ? tarea.checklist : [];

    const checklistNuevo = checklistActual.map((item) =>
      item.id === checklistId
        ? {
            ...item,
            completado: !Boolean(item.completado),
          }
        : item
    );

    const avanceNuevo = calcularAvanceChecklist(checklistNuevo);

    try {
      setLoading(true);

      await apiRequest(`/proyectos/${tarea.proyectoId}/tareas/${tarea.taskId || tarea.id}`, {
        method: "PUT",
        body: JSON.stringify({
          ...tarea,
          checklist: checklistNuevo,
          avance: avanceNuevo,
        }),
      });

      await registrarEventoProyecto(
        tarea.proyectoId,
        "CHECKLIST_UPDATED",
        `Se actualizó checklist de la tarea "${tarea.titulo}".`
      );

      await cargarTareasProyecto(tarea.proyectoId);
    } catch (error) {
      console.error("Error actualizando checklist", error);
      alert("No se pudo actualizar el checklist.");
    } finally {
      setLoading(false);
    }
  }


  async function eliminarChecklistItem(tareaId, checklistId) {
    const tarea = tareas.find((t) => t.id === tareaId || t.taskId === tareaId);
    if (!tarea) return;

    const confirmar = window.confirm("¿Eliminar este punto del checklist?");
    if (!confirmar) return;

    const checklistActual = Array.isArray(tarea.checklist) ? tarea.checklist : [];
    const checklistNuevo = checklistActual.filter((item) => item.id !== checklistId);
    const avanceNuevo = calcularAvanceChecklist(checklistNuevo);

    try {
      setLoading(true);

      await apiRequest(`/proyectos/${tarea.proyectoId}/tareas/${tarea.taskId || tarea.id}`, {
        method: "PUT",
        body: JSON.stringify({
          ...tarea,
          checklist: checklistNuevo,
          avance: avanceNuevo,
        }),
      });

      await registrarEventoProyecto(
        tarea.proyectoId,
        "CHECKLIST_UPDATED",
        `Se eliminó un punto del checklist de la tarea "${tarea.titulo}".`
      );

      await cargarTareasProyecto(tarea.proyectoId);
    } catch (error) {
      console.error("Error eliminando punto de checklist", error);
      alert("No se pudo eliminar el punto del checklist.");
    } finally {
      setLoading(false);
    }
  }

  async function agregarChecklistItem(tareaId) {
    const tarea = tareas.find((t) => t.id === tareaId || t.taskId === tareaId);
    if (!tarea) return;

    const texto = window.prompt("Nuevo punto de checklist:");
    if (!texto) return;

    const checklistActual = Array.isArray(tarea.checklist) ? tarea.checklist : [];

    const checklistNuevo = [
      ...checklistActual,
      {
        id: `CHK-${Date.now()}`,
        texto,
        completado: false,
        orden: checklistActual.length + 1,
      },
    ];

    const avanceNuevo = calcularAvanceChecklist(checklistNuevo);

    try {
      setLoading(true);

      await apiRequest(`/proyectos/${tarea.proyectoId}/tareas/${tarea.taskId || tarea.id}`, {
        method: "PUT",
        body: JSON.stringify({
          ...tarea,
          checklist: checklistNuevo,
          avance: avanceNuevo,
        }),
      });

      await registrarEventoProyecto(
        tarea.proyectoId,
        "CHECKLIST_UPDATED",
        `Se agregó el punto "${texto}" al checklist de la tarea "${tarea.titulo}".`
      );

      await cargarTareasProyecto(tarea.proyectoId);
    } catch (error) {
      console.error("Error agregando checklist", error);
      alert("No se pudo agregar el punto de checklist.");
    } finally {
      setLoading(false);
    }
  }


  const decisionCount = useMemo(() => {
    if (!proyectoActual) return 0;

    const decisionPendiente =
      proyectoActual.decisionRequerida &&
      proyectoActual.decisionRequerida !== "Sin decisión pendiente.";

    const bloqueoActivo = Boolean(proyectoActual.bloqueo);

    const accionPendiente = Boolean(proyectoActual.siguienteAccion);

    const riesgoAtencion =
      proyectoActual.riesgo &&
      proyectoActual.riesgo !== "Bajo";

    return [
      decisionPendiente,
      bloqueoActivo,
      accionPendiente,
      riesgoAtencion,
    ].filter(Boolean).length;
  }, [proyectoActual]);

  function abrirModuloRelacionado(modulo) {
    if (!proyectoActual?.id) {
      alert("Selecciona un proyecto primero.");
      return;
    }

    const proyectoId = encodeURIComponent(proyectoActual.id);
    const proyectoNombre = encodeURIComponent(proyectoActual.nombre || "");

    const rutas = {
      cotizaciones: `/cotizaciones?proyecto=${proyectoId}&nombre=${proyectoNombre}`,
      inventario: `/inventario?proyecto=${proyectoId}&nombre=${proyectoNombre}`,
      documentos: `/documentos?proyecto=${proyectoId}&nombre=${proyectoNombre}`,
    };

    const destino = rutas[modulo];

    if (destino) {
      localStorage.setItem("commandCenterProyectoId", proyectoActual.id);
      localStorage.setItem("commandCenterProyectoNombre", proyectoActual.nombre || "");
      window.location.href = destino;
    }
  }


  function getModuloRelacionadoHref(modulo) {
    if (!proyectoActual?.id) {
      const rutasBase = {
        cotizaciones: "/cotizaciones",
        inventario: "/inventario",
        documentos: "/documentos",
      };

      return rutasBase[modulo] || "/proyectos/command";
    }

    const proyectoId = encodeURIComponent(proyectoActual.id);
    const proyectoNombre = encodeURIComponent(proyectoActual.nombre || "");

    const rutas = {
      cotizaciones: `/cotizaciones?proyecto=${proyectoId}&nombre=${proyectoNombre}`,
      inventario: `/inventario?proyecto=${proyectoId}&nombre=${proyectoNombre}`,
      documentos: `/documentos?proyecto=${proyectoId}&nombre=${proyectoNombre}`,
    };

    return rutas[modulo] || "/proyectos/command";
  }
  // PCC_SAFE_DIRECTION
    // PCC_PORTFOLIO_BUBBLE_ANALYSIS_V1_2
  if (modoCentro === "analisis") {
    return (
      <PortfolioBubbleAnalysisView
        proyectos={proyectos}
        apiRequest={apiRequest}
        onBack={() => setModoCentro("direccion")}
      />
    );
  }
  // PCC_PORTFOLIO_REVIEW_ENTERPRISE_V1_ROUTE
  if (modoCentro === "revision") {
    return (
      <PortfolioReviewView
        proyectos={proyectos}
        apiRequest={apiRequest}
        onBack={() => setModoCentro("direccion")}
      />
    );
  }
if (modoCentro === "direccion") {
    return (
      <PortfolioDirectionView
    focusProjectId={returnFinanceProjectId}
        proyectos={proyectos}
        commercialStages={commercialStages}
        apiRequest={apiRequest}
        
onEditProject={(project) => {
  // PCC_DIRECTION_EDIT_DIRECT_CONFIG_V1
          // El boton Editar abre directamente la configuracion
          // del proyecto exacto seleccionado en el tablero.
          
  // FRONT020D_EDIT_RETURN
  const editProjectId = String(
    project?.id ?? project?.proyectoId ?? ""
  );

  if (editProjectId) {
    setReturnFinanceProjectId(editProjectId);
    setEditReturnPending(true);
  }

  abrirModalEditarProyecto(project);
  setModoCentro("operacion");
}}        onFinanceProject={(project) => {
          // PCC_DIRECTION_FINANCE_BUTTON_V2
          const id = String(
            project?.id ||
            project?.proyectoId ||
            ""
          );

          if (!id) return;

          localStorage.setItem(
            "commandCenterProyectoId",
            id
          );

          localStorage.setItem(
            "commandCenterProyectoNombre",
            project?.nombre || ""
          );

          
    setReturnFinanceProjectId(id);
    setSelectedId(id);
          setPanelTab("finanzas");
          setModoCentro("operacion");
          setFinanceWorkspaceRequest({
            projectId: id,
            token: Date.now(),
          });

          window.setTimeout(() => {
            const tabs =
              document.querySelector(".pcc-panel-tabs");

            if (tabs) {
              tabs.scrollIntoView({
                behavior: "smooth",
                block: "start",
              });
            }
          }, 120);
        }}        onBack={() => setModoCentro("operacion")}
        onAnalysis={() => setModoCentro("analisis")}
        onReview={() => setModoCentro("revision")}
      />
    );
  }

  return (
    <div className="pcc-page">
      <header className="pcc-header">
        <div>
          <p>VISTA PRINCIPAL</p>
          <h1>Tablero por proyectos</h1>
          <span>
            Seguimiento operativo, financiero y directivo del portafolio.
          </span>
        </div>

        <div className="pcc-header-actions">
          <button
            type="button"
            className="pcc-executive-report-btn"
            onClick={() => setModoCentro("direccion")}
          >
            Vista direccion
          </button>
          <button className="pcc-executive-report-btn" onClick={handleExecutiveReportPdf} disabled={executiveReportLoading}>
                    {executiveReportLoading ? "Preparando informe..." : "Informe ejecutivo"}
                  </button>
          {/* PCC_ENTERPRISE_REFRESH_V4_5B */}
          <button
            type="button"
            className="pcc-refresh-btn"
            onClick={cargarProyectos}
            disabled={loading}
            aria-busy={loading}
            title="Actualizar portafolio"
          >
            <span
              className={
                "pcc-refresh-icon" +
                (loading ? " is-loading" : "")
              }
              aria-hidden="true"
            >
              ↻
            </span>

            <span>
              {loading ? "Actualizando" : "Actualizar"}
            </span>
          </button>
          <button
            type="button"
            className="primary"
            onClick={() => {
                setProjectEditId(null);
                abrirModalNuevoProyecto();
              }}
          >
            Nuevo proyecto
          </button>
        </div>
      </header>

            {(loading || syncError) && (
        <div
          className={
            "pcc-enterprise-sync" +
            (syncError ? " has-error" : "")
          }
          role="status"
          aria-live="polite"
        >
          {loading && (
            <>
              <div className="pcc-enterprise-sync-body">

                <div
                  className="pcc-enterprise-loader"
                  aria-hidden="true"
                >
                  <span className="pcc-loader-ring"></span>
                  <span className="pcc-loader-ring inner"></span>
                  <span className="pcc-loader-core"></span>
                </div>

                <div className="pcc-enterprise-sync-text">
                  <strong>Sincronizando portafolio</strong>
                  <span>Consolidando información operativa y financiera</span>
                </div>

                <div
                  className="pcc-enterprise-live"
                  aria-hidden="true"
                >
                  <span></span>
                  <span></span>
                  <span></span>
                </div>

              </div>

              <div
                className="pcc-enterprise-progress"
                aria-hidden="true"
              >
                <i></i>
              </div>
            </>
          )}

          {syncError && (
            <div className="pcc-enterprise-error">
              <span className="pcc-enterprise-error-symbol">
                !
              </span>

              <div>
                <strong>
                  No fue posible actualizar el portafolio
                </strong>

                <span>
                  {syncError}
                </span>
              </div>
            </div>
          )}
        </div>
      )}
<section className="pcc-metrics">
        <article>
          <span>Activos</span>
          <strong>{metricas.activos}</strong>
          <small>proyectos abiertos</small>
        </article>
        <article className="warn">
          <span>Bloqueados</span>
          <strong>{metricas.bloqueados}</strong>
          <small>requieren atención</small>
        </article>
        <article className="danger">
          <span>Vencidos</span>
          <strong>{metricas.vencidos}</strong>
          <small>compromisos fuera de fecha</small>
        </article>
        <article>
          <span>Decisiones</span>
          <strong>{metricas.decisiones}</strong>
          <small>pendientes directivas</small>
        </article>
        <article className="dark">
          <span>Avance promedio</span>
          <strong>{metricas.avancePromedio}%</strong>
          <small>portafolio general</small>
        </article>
      </section>

      <section className="pcc-toolbar pcc-toolbar-clean">
        <div className="pcc-toolbar-left">
          <div className="pcc-tabs">
            <button
              className={vista === "kanban" ? "active" : ""}
              onClick={() => setVista("kanban")}
            >
              Kanban
            </button>

            <button
              className={vista === "lista" ? "active" : ""}
              onClick={() => setVista("lista")}
            >
              Lista
            </button>

            <button
              className={vista === "riesgos" ? "active" : ""}
              onClick={() => setVista("riesgos")}
            >
              Riesgos
            </button>
          </div>
        </div>

        <div className="pcc-toolbar-search">
          <input
            className="pcc-project-search"
            value={filtro}
            onChange={(e) => setFiltro(e.target.value)}
            placeholder="Buscar proyecto, cliente, responsable, fase..."
          />
        </div>
      </section>

      <main className="pcc-layout">
        <section className="pcc-main">
          {vista === "kanban" && (
            <div className="pcc-kanban">
              {FASES.map((fase) => {
                const items = proyectosFiltrados.filter((p) => p.fase === fase);

              
  async function crearTareaSeguimientoOperativo() {
    if (!proyectoActual) return;

    const modo = inferirModoOperativo(proyectoActual);
    const dependencia = inferirDependenciaPrincipal(proyectoActual);
    const evidencia = inferirEvidenciaFaltante(proyectoActual);

    try {
      setLoading(true);

      await apiRequest(`/proyectos/${proyectoActual.id}/tareas`, {
        method: "POST",
        body: JSON.stringify({
          titulo: `Seguimiento operativo: ${modo}`,
          descripcion: `Dependencia principal: ${dependencia}. Evidencia faltante: ${evidencia}. Proyecto: ${proyectoActual.nombre}.`,
          responsable: proyectoActual.responsable || "Sin asignar",
          prioridad: proyectoActual.prioridad || "Media",
          estado: "Pendiente",
          fechaLimite: proyectoActual.fechaCompromiso || "",
          checklist: [
            `Confirmar dependencia: ${dependencia}`,
            `Obtener evidencia: ${evidencia}`,
            "Actualizar siguiente movimiento",
            "Registrar avance o bloqueo"
          ],
          tags: ["seguimiento", "work-os", "proyectos"]
        }),
      });

      await cargarTareasProyecto(proyectoActual.id);
    } catch (error) {
      console.error("Error creando tarea de seguimiento", error);
      alert("No se pudo crear la tarea de seguimiento.");
    } finally {
      setLoading(false);
    }
  }

  async function solicitarAutorizacionOperativa() {
    if (!proyectoActual) return;

    try {
      setLoading(true);

      await apiRequest(`/proyectos/${proyectoActual.id}/tareas`, {
        method: "POST",
        body: JSON.stringify({
          titulo: "Solicitar autorización formal",
          descripcion: `Preparar solicitud de autorización para avanzar el proyecto: ${proyectoActual.nombre}.`,
          responsable: proyectoActual.responsable || "Sin asignar",
          prioridad: "Alta",
          estado: "Pendiente",
          fechaLimite: proyectoActual.fechaCompromiso || "",
          checklist: [
            "Preparar justificación",
            "Adjuntar evidencia técnica",
            "Solicitar autorización",
            "Registrar respuesta"
          ],
          tags: ["autorizacion", "direccion", "proyectos"]
        }),
      });

      await cargarTareasProyecto(proyectoActual.id);
    } catch (error) {
      console.error("Error solicitando autorización", error);
      alert("No se pudo crear la tarea de autorización.");
    } finally {
      setLoading(false);
    }
  }


  return (
                  <div
                  className={cx(
                    "pcc-column",
                    dragOverFase === fase && "drag-over",
                    getColumnWipClass(fase, items.length)
                  )}
                  key={fase}
                  onDragOver={(event) => onDragOverFase(event, fase)}
                  onDragLeave={() => setDragOverFase("")}
                  onDrop={(event) => onDropFase(event, fase)}
                >
                    <div className="pcc-column-head">
                      <strong>{fase}</strong>
                      <span>{getColumnWipLabel(fase, items.length)}</span>
                    </div>

                    <div className="pcc-cards">
                      {items.map((p) => (
                        <article
                          key={p.id}
                          draggable
                          onDragStart={(event) => onDragStartProyecto(event, p.id)}
                          onDragEnd={onDragEndProyecto}
                          className={cx(
                            "pcc-card",
                            p.id === selectedId && "selected",
                            dragProyectoId === p.id && "dragging"
                          )}
                          onClick={() => seleccionarProyectoPorId(p.id)}
                        >
                          <div className="pcc-card-top">
                            <strong>{p.nombre}</strong>
                            <span className={cx("pcc-priority", prioridadClass(p.prioridad))}>
                              {p.prioridad}
                            </span>
                          </div>

                          <p>{p.cliente}</p>

                          <div className="pcc-progress">
                            <div>
                              <span style={{ width: `${p.avance || 0}%` }} />
                            </div>
                            <small>{p.avance}%</small>
                          </div>

                          <div className="pcc-card-intel">
                            <div>
                              <span>Modo</span>
                              <strong>{inferirModoOperativo(p)}</strong>
                            </div>

                            <div>
                              <span>Siguiente</span>
                              <strong title={p.siguienteAccion || p.proximoPaso || ""}>
                                {compactText(p.siguienteAccion || p.proximoPaso || "Sin siguiente acción", 58)}
                              </strong>
                            </div>
                          </div>

                          <div className="pcc-card-meta">
                            <span>{p.responsable}</span>
                            <b className={isVencido(p.fechaCompromiso) ? "late" : ""}>
                              {p.fechaCompromiso || "Sin compromiso"}
                            </b>
                          </div>

                          <div className="pcc-card-lifecycle">
                            <span>Inicio · {formatProjectDate(p.createdAt || p.fechaInicio || p.fechaAlta)}</span>
                            <strong>Vida · {getProjectLifeDays(p) ?? "N/D"} días</strong>
                          </div>

                          <div className="pcc-card-healthline">
                            <span>{getProjectLastUpdateLabel(p)}</span>
                            <strong className={getProjectHealthClass(p)}>
                              Salud · {getProjectHealthLabel(p)}
                            </strong>
                          </div>

                          <div className="pcc-card-sla-aging">
                            <span className={getSlaClass(p)}>{getSlaLabel(p)}</span>
                            <strong className={getPhaseAgingClass(p)}>{getPhaseAgingLabel(p)}</strong>
                          </div>

                          {p.bloqueo && <div className="pcc-blocker">Bloqueo: {p.bloqueo}</div>}
                          <div
                            className="pcc-card-actions"
                            onClick={(event) => detenerEventoCard(event)}
                            onMouseDown={(event) => detenerEventoCard(event)}
                            onPointerDown={(event) => event.stopPropagation()}
                            draggable={false}
                          >
                                                          <button
                                type="button"
                                draggable={false}
                                onMouseDown={(event) => detenerEventoCard(event)}
                                onPointerDown={(event) => event.stopPropagation()}
                                onClick={(event) => {
                                  detenerEventoCard(event);
                                  abrirModalEditarProyecto(p);
                                }}
                              >
                                Editar
                              </button>
<button
                              type="button"
                              draggable={false}
                              onMouseDown={(event) => detenerEventoCard(event)}
                              onPointerDown={(event) => event.stopPropagation()}
                              onClick={(event) => abrirTabProyectoDesdeCard(event, p, "finanzas")}
                            >
                              Finanzas
                            </button>

                            <button
                              type="button"
                              draggable={false}
                              onMouseDown={(event) => detenerEventoCard(event)}
                              onPointerDown={(event) => event.stopPropagation()}
                              onClick={(event) => {
                                detenerEventoCard(event);
                                seleccionarProyectoPorId(p.id);
                                setPanelTab("tareas");
                                scrollInspectorProyecto();
                                agregarTareaRapidaProyecto(p);
                              }}
                            >
                              + Tarea
                            </button>

                            <button
                              type="button"
                              draggable={false}
                              onMouseDown={(event) => detenerEventoCard(event)}
                              onPointerDown={(event) => event.stopPropagation()}
                              onClick={(event) => abrirTabProyectoDesdeCard(event, p, "decisiones")}
                            >
                              Decisiones
                            </button>

                            <button
                              type="button"
                              draggable={false}
                              onMouseDown={(event) => detenerEventoCard(event)}
                              onPointerDown={(event) => event.stopPropagation()}
                              onClick={(event) => abrirTabProyectoDesdeCard(event, p, "bitacora")}
                            >
                              Bitacora
                            </button>
                          </div></article>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {vista === "lista" && (
            <table className="pcc-table">
              <thead>
                <tr>
                  <th>Proyecto</th>
                  <th>Cliente</th>
                  <th>Fase</th>
                  <th>Prioridad</th>
                  <th>Avance</th>
                  <th>Compromiso</th>
                  <th>Decisión</th>
                </tr>
              </thead>
              <tbody>
                {proyectosFiltrados.map((p) => (
                  <tr key={p.id} onClick={() => seleccionarProyectoPorId(p.id)}>
                    <td><strong>{p.nombre}</strong></td>
                    <td>{p.cliente}</td>
                    <td>{p.fase}</td>
                    <td>{p.prioridad}</td>
                    <td>{p.avance}%</td>
                    <td className={isVencido(p.fechaCompromiso) ? "late" : ""}>{p.fechaCompromiso}</td>
                    <td>{p.decisionRequerida || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {vista === "riesgos" && (
            <div className="pcc-risk-board">
              {proyectosFiltrados
                .filter((p) => p.bloqueo || p.riesgo !== "Bajo" || isVencido(p.fechaCompromiso))
                .map((p) => (
                  <article key={p.id} onClick={() => seleccionarProyectoPorId(p.id)}>
                    <span className={cx("pcc-risk", riesgoClass(p.riesgo))}>{p.riesgo}</span>
                    <h3>{p.nombre}</h3>
                    <p>{p.bloqueo || p.siguienteAccion}</p>
                    <small>Decisión: {p.decisionRequerida || "Sin decisión pendiente"}</small>
                  </article>
                ))}
            </div>
          )}
        </section>

        <aside className="pcc-detail">
          {proyectoActual ? (
            <>
              <div className="pcc-detail-head">
                <span>Proyecto seleccionado</span>
                <h2>{proyectoActual.nombre}</h2>
                <p>{proyectoActual.cliente}</p>
              </div>

              <div className="pcc-detail-grid">
                <div>
                  <span>Fase</span>
                  <strong>{proyectoActual.fase}</strong>
                </div>
                <div>
                  <span>Avance</span>
                  <strong>{proyectoActual.avance}%</strong>
                </div>
                <div>
                  <span>Prioridad</span>
                  <strong>{proyectoActual.prioridad}</strong>
                </div>
                <div>
                  <span>Riesgo</span>
                  <strong>{proyectoActual.riesgo}</strong>
                </div>
              </div>

              <section className="pcc-directive-box">
                <span>Siguiente acción</span>
                <p>{proyectoActual.siguienteAccion || "Sin siguiente acción registrada."}</p>
              </section>

              <section className="pcc-directive-box decision">
                <span>Decisión requerida</span>
                <p>{proyectoActual.decisionRequerida || "Sin decisión pendiente."}</p>
              </section>

              <section className="pcc-lifecycle-panel">
                <div className="pcc-section-title mini">
                  <span>Linea de tiempo</span>
                  <strong>Vida operativa del proyecto</strong>
                </div>

                <div className="pcc-lifecycle-grid">
                  <article>
                    <span>Fecha de alta</span>
                    <strong>{formatProjectDate(proyectoActual.createdAt || proyectoActual.fechaInicio || proyectoActual.fechaAlta)}</strong>
                  </article>

                  <article>
                    <span>Tiempo de vida</span>
                    <strong>{getProjectLifeLabel(proyectoActual)}</strong>
                  </article>

                  <article>
                    <span>Ultima actualizacion</span>
                    <strong>{formatProjectDate(proyectoActual.updatedAt)}</strong>
                    <small>{getProjectLastUpdateLabel(proyectoActual)}</small>
                  </article>

                  <article>
                    <span>Fecha compromiso</span>
                    <strong>{proyectoActual.fechaCompromiso || "Sin compromiso"}</strong>
                  </article>
                </div>

                <div className="pcc-lifecycle-sla-row">
                  <span className={getSlaClass(proyectoActual)}>{getSlaLabel(proyectoActual)}</span>
                  <strong className={getPhaseAgingClass(proyectoActual)}>{getPhaseAgingLabel(proyectoActual)}</strong>
                </div>

                <div className="pcc-lifecycle-line">
                  <div className="pcc-life-step done">
                    <b></b>
                    <span>Alta</span>
                    <small>{formatProjectDate(proyectoActual.createdAt || proyectoActual.fechaInicio || proyectoActual.fechaAlta)}</small>
                  </div>

                  <div className="pcc-life-step active">
                    <b></b>
                    <span>{proyectoActual.fase || "Fase actual"}</span>
                    <small>{getProjectLifeLabel(proyectoActual)}</small>
                  </div>

                  <div className="pcc-life-step">
                    <b></b>
                    <span>Compromiso</span>
                    <small>{proyectoActual.fechaCompromiso || "Sin fecha"}</small>
                  </div>
                </div>
              </section>

              {proyectoActual.bloqueo && (
                <section className="pcc-directive-box blocker">
                  <span>Bloqueo</span>
                  <p>{proyectoActual.bloqueo}</p>
                </section>
              )}

                            <div className="pcc-panel-tabs">
                <button
                  type="button"
                  className={cx(panelTab === "resumen" && "active")}
                  onClick={() => setPanelTab("resumen")}
                >
                  Resumen
                </button>
                <button
                  type="button"
                  className={cx(panelTab === "tareas" && "active")}
                  onClick={() => setPanelTab("tareas")}
                >
                  Tareas <span>{tareasProyecto.length}</span>
                </button>
                <button
                  type="button"
                  className={cx(panelTab === "bitacora" && "active")}
                  onClick={() => setPanelTab("bitacora")}
                >
                  Bitácora <span>{eventos.length}</span>
                </button>
                <button
                  type="button"
                  className={cx(panelTab === "decisiones" && "active", decisionCount > 0 && "has-alert")}
                  onClick={() => setPanelTab("decisiones")}
                >
                  Decisiones <span>{decisionCount}</span>
                </button>
                <button
                  type="button"
                  className={cx(panelTab === "finanzas" && "active")}
                  onClick={() => setPanelTab("finanzas")}
                >
                  Finanzas
                </button>
              </div>
              <section className={cx("pcc-operational-fields", panelTab !== "resumen" && "pcc-hidden")}>
                <div className="pcc-section-title">
                  <span>Work OS</span>
                  <strong>Campos operativos</strong>
                </div>

                <div className="pcc-op-grid">
                  <article>
                    <span>Modo operativo</span>
                    <strong>{inferirModoOperativo(proyectoActual)}</strong>
                  </article>

                  <article>
                    <span>Dependencia</span>
                    <strong>{inferirDependenciaPrincipal(proyectoActual)}</strong>
                  </article>

                  <article>
                    <span>Evidencia faltante</span>
                    <strong>{inferirEvidenciaFaltante(proyectoActual)}</strong>
                  </article>

                  <article>
                    <span>Automatización sugerida</span>
                    <strong>{inferirAutomatizacionSugerida(proyectoActual)}</strong>
                  </article>
                </div>

                <div className="pcc-connections">
                  <span>Conexiones</span>
                  <a href={getModuloRelacionadoHref("cotizaciones")}>Cotizaciones</a>
                  <a href={getModuloRelacionadoHref("inventario")}>Inventario</a>
                  <a href={getModuloRelacionadoHref("documentos")}>Documentos</a>
                </div>
              </section>

                                          <ProyectoFinanzasPanel
    onWorkspaceClose={() => {
    setModoCentro("direccion");
  }}
                key={proyectoActual.id || proyectoActual.proyectoId}
                proyecto={proyectoActual}
                visible={panelTab === "finanzas"}
                workspaceRequest={financeWorkspaceRequest}
/>

<section className={cx("pcc-decision-board", panelTab !== "decisiones" && "pcc-hidden")}>
                <div className="pcc-section-title">
                  <span>Decisiones</span>
                  <strong>Bloqueos y autorizaciones</strong>
                </div>

                <div className="pcc-decision-stack">
                  <article className="pcc-decision-card main">
                    <span>Decisión requerida</span>
                    <strong>{proyectoActual.decisionRequerida || "Sin decisión pendiente."}</strong>
                    <p>
                      {proyectoActual.decisionRequerida
                        ? "Esta decisión debe cerrarse para evitar fricción operativa."
                        : "No hay una decisión crítica registrada en este momento."}
                    </p>
                  </article>

                  <article className="pcc-decision-card">
                    <span>Bloqueo actual</span>
                    <strong>{proyectoActual.bloqueo || "Sin bloqueo declarado."}</strong>
                    <p>
                      {proyectoActual.bloqueo
                        ? "El proyecto requiere seguimiento hasta liberar este bloqueo."
                        : "El proyecto puede continuar sin bloqueo operativo declarado."}
                    </p>
                  </article>

                  <article className="pcc-decision-card">
                    <span>Siguiente acción</span>
                    <strong>{proyectoActual.siguienteAccion || "Sin siguiente acción."}</strong>
                    <p>Acción recomendada para mantener avance y trazabilidad.</p>
                  </article>

                  <article className="pcc-decision-card">
                    <span>Escalamiento sugerido</span>
                    <strong>{inferirAutomatizacionSugerida(proyectoActual)}</strong>
                    <p>Regla operativa sugerida por el Command Center.</p>
                  </article>
                </div>
              </section>
              <section className={cx("pcc-timeline", panelTab !== "bitacora" && "pcc-hidden")}>
                <div className="pcc-section-title">
                  <span>Bitácora</span>
                  <strong>Últimas modificaciones</strong>
                </div>

                {eventos.length === 0 ? (
                  <p className="pcc-empty">Sin movimientos registrados.</p>
                ) : (
                  <div className="pcc-timeline-list">
                    {eventos.slice(0, 8).map((ev, index) => (
                      <article key={ev.id || ev.eventoId || `${ev.tipo}-${index}`} className="pcc-timeline-item">
                        <div className="pcc-timeline-dot" />
                        <div>
                          <strong>{tipoVisibleEvento(ev.tipo)}</strong>
                          <p>{ev.descripcion || ev.detalle || ev.message || "Movimiento registrado en el proyecto."}</p>
                          <small>
                            {fechaVisibleEvento(ev)} · {usuarioVisibleEvento(ev)}
                          </small>
                        </div>
                      </article>
                    ))}
                  </div>
                )}
              </section>
              <section className={cx("pcc-tasks", panelTab !== "tareas" && "pcc-hidden")}>
                <div className="pcc-tasks-head">
                  <h3>Tareas</h3>
                  <button onClick={agregarTareaRapida}>+ Tarea</button>
                </div>

                {tareasProyecto.length === 0 ? (
                  <p className="pcc-empty">Sin tareas registradas.</p>
                ) : (
                  tareasProyecto.map((t) => {
                    const checklist = Array.isArray(t.checklist) ? t.checklist : [];
                    const avanceChecklist = calcularAvanceChecklist(checklist);

                    return (
                      <article key={t.id} className="pcc-task-card">
                        <div className="pcc-task-main">
                          <div>
                            <strong>{t.titulo}</strong>
                            <span>{t.responsable} · {t.fechaLimite || "Sin fecha"}</span>
                          </div>

                          <select
                            value={t.estado}
                            onChange={(e) => cambiarEstadoTarea(t.id, e.target.value)}
                          >
                            <option>Pendiente</option>
                            <option>En proceso</option>
                            <option>Completada</option>
                            <option>Bloqueada</option>
                          </select>
                        </div>

                        <div className="pcc-task-progress">
                          <div>
                            <span style={{ width: `${avanceChecklist}%` }} />
                          </div>
                          <small>{avanceChecklist}% checklist</small>
                        </div>

                        <div className="pcc-checklist">
                          <div className="pcc-checklist-head">
                            <span>Checklist</span>
                            <button type="button" onClick={() => agregarChecklistItem(t.id)}>
                              + Punto
                            </button>
                          </div>

                          {checklist.length === 0 ? (
                            <p>Sin checklist.</p>
                          ) : (
                            checklist.map((item) => (
                              <div key={item.id} className={`pcc-checklist-row ${item.completado ? "done" : ""}`}>
                                <label>
                                  <input
                                    type="checkbox"
                                    checked={Boolean(item.completado)}
                                    onChange={() => toggleChecklistItem(t.id, item.id)}
                                  />
                                  <span>{item.texto}</span>
                                </label>

                                <button
                                  type="button"
                                  className="pcc-checklist-delete"
                                  onClick={() => eliminarChecklistItem(t.id, item.id)}
                                  title="Eliminar punto"
                                >
                                  ×
                                </button>
                              </div>
                            ))
                          )}
                        </div>
                      </article>
                    );
                  })
                )}
              </section>
            </>
          ) : (
            <p className="pcc-empty">Selecciona un proyecto.</p>
          )}
        </aside>
      </main>

      {projectModal && (
        <div
          className="pcc-project-modal-backdrop"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              cerrarModalNuevoProyecto();
            }
          }}
        >
          <form
            className="pcc-project-modal"
            onSubmit={crearProyecto}
            role="dialog"
            aria-modal="true"
            aria-labelledby="pcc-new-project-title"
          >
            <div className="pcc-project-modal-header">
              <div>
                <span>Gestión de Proyectos</span>
                <h2 id="pcc-new-project-title">
                    {projectEditId
                      ? "Editar proyecto"
                      : "Nuevo proyecto"}
                  </h2>
              </div>

              <button
                type="button"
                className="pcc-project-modal-close"
                onClick={cerrarModalNuevoProyecto}
                disabled={projectCreateLoading}
                aria-label="Cerrar formulario"
              >
                ×
              </button>
            </div>

            <div className="pcc-project-modal-body pcc-wizard-body">

              <div className="pcc-wizard-progress" aria-label="Progreso de captura">
                {[
                  { id: 1, label: "Datos generales" },
                  { id: 2, label: "Situacion" },
                  { id: 3, label: "Informacion" },
                  { id: 4, label: "Confirmar" },
                ].map((item) => (
                  <div
                    key={item.id}
                    className={
                      "pcc-wizard-progress-item " +
                      (projectWizardStep === item.id
                        ? "active"
                        : projectWizardStep > item.id
                          ? "complete"
                          : "")
                    }
                  >
                    <span className="pcc-wizard-progress-number">
                      {projectWizardStep > item.id ? "\u2713" : item.id}
                    </span>

                    <span className="pcc-wizard-progress-label">
                      {item.label}
                    </span>
                  </div>
                ))}
              </div>

              {projectWizardStep === 1 && (
                <section className="pcc-wizard-panel">
                  <div className="pcc-wizard-step-hero">
                    <span>PASO 1 DE 4</span>

                    <h3>Datos generales</h3>

                    <p>
                      Captura solamente la informacion basica para
                      identificar y asignar el proyecto.
                    </p>
                  </div>

                  <div className="pcc-wizard-fields pcc-wizard-fields-two">

                    <label className="pcc-project-field pcc-project-field-full">
                      <span>Nombre del proyecto *</span>

                      <input
                        autoFocus
                        value={projectForm.nombre}
                        onChange={(event) =>
                          setProjectForm((current) => ({
                            ...current,
                            nombre: event.target.value,
                          }))
                        }
                        placeholder="Ej. Sistema integral de control"
                      />
                    </label>

                    <label className="pcc-project-field">
                      <span>Cliente</span>

                      <input
                        value={projectForm.cliente}
                        onChange={(event) =>
                          setProjectForm((current) => ({
                            ...current,
                            cliente: event.target.value,
                          }))
                        }
                        placeholder="Cliente o institucion"
                      />
                    </label>

                    <label className="pcc-project-field">
                      <span>Responsable *</span>

                      <input
                        value={projectForm.responsable}
                        onChange={(event) =>
                          setProjectForm((current) => ({
                            ...current,
                            responsable: event.target.value,
                          }))
                        }
                        placeholder="Responsable principal"
                      />
                    </label>

                    <label className="pcc-project-field">
                      <span>Fecha compromiso</span>

                      <small className="pcc-wizard-field-help">
                        Proxima fecha importante que debemos cumplir.
                      </small>

                      <input
                        type="date"
                        value={projectForm.fechaCompromiso}
                        onChange={(event) =>
                          setProjectForm((current) => ({
                            ...current,
                            fechaCompromiso: event.target.value,
                          }))
                        }
                      />
                    </label>

                    <label className="pcc-project-field">
                      <span>Prioridad</span>

                      <select
                        value={projectForm.prioridad}
                        onChange={(event) =>
                          setProjectForm((current) => ({
                            ...current,
                            prioridad: event.target.value,
                          }))
                        }
                      >
                        <option value="ALTA">Alta</option>
                        <option value="MEDIA">Media</option>
                        <option value="BAJA">Baja</option>
                      </select>
                    </label>

                  </div>

                  {!getProjectWizardStepState(
                    projectForm,
                    1
                  ).valid && (
                    <div className="pcc-wizard-inline-status">
                      Completa:{" "}
                      {getProjectWizardStepState(
                        projectForm,
                        1
                      ).missing.join(", ")}
                    </div>
                  )}
                </section>
              )}

              {projectWizardStep === 2 && (
                <section className="pcc-wizard-panel">
                  <div className="pcc-wizard-step-hero">
                    <span>PASO 2 DE 4</span>

                    <h3>
                      {"\u00BFEn qu\u00E9 situaci\u00F3n est\u00E1 el proyecto?"}
                    </h3>

                    <p>
                      Esta decision determina que informacion
                      solicitara el sistema en el siguiente paso.
                    </p>
                  </div>

                  <div className="pcc-wizard-situation-list">

  <button
    type="button"
    className={
      "pcc-wizard-situation-option " +
      (
        normalizeProjectSituation(
          projectForm.situacionProyecto
        ) === PROJECT_SITUATIONS.VENTA_ACTIVA
          ? "active"
          : ""
      )
    }
    onClick={() =>
      setProjectForm((current) => ({
        ...current,
        situacionProyecto:
          PROJECT_SITUATIONS.VENTA_ACTIVA,
        etapa: "Idea",
        resultadoCierre: "",
        fechaCierre: "",
        motivoCierre: "",
        etapaComercial:
          current.etapaComercial || "OPORTUNIDAD",
      }))
    }
  >
    <span className="pcc-wizard-radio" />

    <span className="pcc-wizard-situation-copy">
      <strong>Venta activa</strong>

      <span>
        Oportunidad comercial actualmente en seguimiento.
      </span>
    </span>
  </button>

  <button
    type="button"
    className={
      "pcc-wizard-situation-option " +
      (
        normalizeProjectSituation(
          projectForm.situacionProyecto
        ) === PROJECT_SITUATIONS.PAUSADO
          ? "active"
          : ""
      )
    }
    onClick={() =>
      setProjectForm((current) => ({
        ...current,
        situacionProyecto:
          PROJECT_SITUATIONS.PAUSADO,
        etapa: "Idea",
        resultadoCierre: "",
        fechaCierre: "",
        motivoCierre: "",
        etapaComercial:
          current.etapaComercial || "OPORTUNIDAD",
      }))
    }
  >
    <span className="pcc-wizard-radio" />

    <span className="pcc-wizard-situation-copy">
      <strong>Venta detenida temporalmente</strong>

      <span>
        La oportunidad sigue vigente, pero está temporalmente pausada.
      </span>
    </span>
  </button>

  <button
    type="button"
    className={
      "pcc-wizard-situation-option " +
      (
        normalizeProjectSituation(
          projectForm.situacionProyecto
        ) === PROJECT_SITUATIONS.EJECUCION
          ? "active"
          : ""
      )
    }
    onClick={() =>
      setProjectForm((current) => ({
        ...current,
        situacionProyecto:
          PROJECT_SITUATIONS.EJECUCION,
        etapaComercial: "GANADO",
        probabilidadComercial: 100,
        resultadoCierre: "",
        fechaCierre: "",
        motivoCierre: "",
        etapa:
          current.etapa === "Idea" ||
          current.etapa === "Cerrado"
            ? "Planeacion"
            : current.etapa,
      }))
    }
  >
    <span className="pcc-wizard-radio" />

    <span className="pcc-wizard-situation-copy">
      <strong>En ejecución</strong>

      <span>
        Ya existe formalización para ejecutar el proyecto.
      </span>
    </span>
  </button>

  <button
    type="button"
    className={
      "pcc-wizard-situation-option " +
      (
        normalizeProjectSituation(
          projectForm.situacionProyecto
        ) === PROJECT_SITUATIONS.CERRADO
          ? "active"
          : ""
      )
    }
    onClick={() =>
      setProjectForm((current) => ({
        ...current,
        situacionProyecto:
          PROJECT_SITUATIONS.CERRADO,
        etapa: "Cerrado",
        etapaComercial: "",
        resultadoCierre: "CERRADO",
      }))
    }
  >
    <span className="pcc-wizard-radio" />

    <span className="pcc-wizard-situation-copy">
      <strong>Cerrado satisfactoriamente</strong>

      <span>
        El proyecto concluyó correctamente.
      </span>
    </span>
  </button>

  <button
    type="button"
    className={
      "pcc-wizard-situation-option " +
      (
        normalizeProjectSituation(
          projectForm.situacionProyecto
        ) === PROJECT_SITUATIONS.NO_REALIZADA
          ? "active"
          : ""
      )
    }
    onClick={() =>
      setProjectForm((current) => ({
        ...current,
        situacionProyecto:
          PROJECT_SITUATIONS.NO_REALIZADA,
        etapa: "Cerrado",
        etapaComercial: "",
        resultadoCierre: "PERDIDO",
      }))
    }
  >
    <span className="pcc-wizard-radio" />

    <span className="pcc-wizard-situation-copy">
      <strong>Venta no realizada</strong>

      <span>
        La oportunidad comercial terminó sin concretarse.
      </span>
    </span>
  </button>

  <button
    type="button"
    className={
      "pcc-wizard-situation-option " +
      (
        normalizeProjectSituation(
          projectForm.situacionProyecto
        ) === PROJECT_SITUATIONS.MUERTO
          ? "active"
          : ""
      )
    }
    onClick={() =>
      setProjectForm((current) => ({
        ...current,
        situacionProyecto:
          PROJECT_SITUATIONS.MUERTO,
        etapa: "Cerrado",
        etapaComercial: "",
        resultadoCierre: "MUERTO",
      }))
    }
  >
    <span className="pcc-wizard-radio" />

    <span className="pcc-wizard-situation-copy">
      <strong>Muerto / Sin continuidad</strong>

      <span>
        La iniciativa fue cancelada, descartada o dejó de ser viable.
      </span>
    </span>
  </button>

</div>
<div className="pcc-wizard-selection-summary">
                    <span>Seleccion actual</span>

                    <strong>
                      {getProjectSituationLabel(
                        projectForm.situacionProyecto
                      )}
                    </strong>
                  </div>
                </section>
              )}

              {projectWizardStep === 3 && (
                <section className="pcc-wizard-panel">
                  <div className="pcc-wizard-step-hero">
                    <span>PASO 3 DE 4</span>

                    <h3>
  {getProjectStepThreeTitle(
    projectForm.situacionProyecto
  )}
</h3>

                    <p>
                      El sistema muestra unicamente los datos
                      necesarios para la situacion seleccionada.
                    </p>
                  </div>

                  <div className="pcc-wizard-fields pcc-wizard-fields-two">

                    <label className="pcc-project-field">
                      <span>Tipo de proyecto</span>

                      <select
                        value={projectForm.tipoProyecto}
                        onChange={(event) =>
                          setProjectForm((current) => ({
                            ...current,
                            tipoProyecto: event.target.value,
                          }))
                        }
                      >
                        <option value="EXTERNO">
                          Externo / Cliente
                        </option>

                        <option value="INHOUSE">
                          Interno / Inhouse
                        </option>
                      </select>
                    </label>

                    {isCommercialProjectSituation(
                      projectForm.situacionProyecto
                    ) && (
                      <>
                        <label className="pcc-project-field">
                          <span>Etapa comercial *</span>

                          <select
                            value={projectForm.etapaComercial}
                            onChange={(event) => {
                              const etapaComercial =
                                event.target.value;

                              const probabilidadComercial =
                                getCommercialProbability(etapaComercial, commercialStages);

                              setProjectForm((current) => ({
                                ...current,
                                etapaComercial,
                                probabilidadComercial,
                              }));
                            }}
                          >
                            {commercialStages.map((stage) => (
                              <option
                                key={stage.codigo}
                                value={stage.codigo}
                              >
                                {(stage.orden ? stage.orden + ". " : "") +
                                  (stage.nombre || stage.codigo)}
                              </option>
                            ))}
                          </select>
                        </label>

                        <label className="pcc-project-field">
                          <span>Valor estimado del proyecto *</span>

                          <small className="pcc-wizard-field-help">
                            Valor aproximado antes de formalizar
                            el contrato.
                          </small>

                          <div className="pcc-project-money-field">

                            <div className="pcc-project-money-input-wrap">

                              <span className="pcc-project-money-prefix">
                                $
                              </span>

                              <input
                                type="text"
                                inputMode="decimal"
                                autoComplete="off"
                                value={projectForm.montoOportunidad}
                                onChange={(event) => {
                                  const normalized =
                                    normalizeProjectMoneyInput(
                                      event.target.value
                                    );

                                  setProjectForm((current) => ({
                                    ...current,
                                    montoOportunidad:
                                      normalized,
                                  }));
                                }}
                                placeholder="20000000"
                                aria-label="Valor estimado del proyecto en pesos"
                              />

                              <span className="pcc-project-money-currency">
                                MXN
                              </span>

                            </div>

                            <div className="pcc-project-money-confirmation">

                              <span>
                                Monto interpretado
                              </span>

                              <strong>
                                {formatProjectMoneyExact(
                                  projectForm.montoOportunidad
                                )}
                              </strong>

                              <em>
                                {formatProjectMoneyMdp(
                                  projectForm.montoOportunidad
                                )}
                              </em>

                            </div>

                          </div>
                        </label>

                        <div className="pcc-wizard-calculation">
                          <span>Probabilidad</span>

                          <strong>
                            {getCommercialProbability(projectForm.etapaComercial, commercialStages)}
                            %
                          </strong>

                          <small>
                            Calculada automaticamente por etapa.
                          </small>
                        </div>

                        <div className="pcc-wizard-calculation">
                          <span>Pipeline ponderado</span>

                          <strong>
                            {formatProjectMoney(
                              getWeightedPipeline(
                                projectForm.montoOportunidad,
                                getCommercialProbability(projectForm.etapaComercial, commercialStages)
                              )
                            )}
                          </strong>

                          <small>
                            Valor estimado x probabilidad.
                          </small>
                        </div>

                        {projectForm.etapaComercial ===
                          "CONTRATACION" && (
                          <div className="pcc-wizard-warning pcc-project-field-full">
                            <strong>Importante</strong>

                            <span>
                              Si ya existe contrato, adjudicacion,
                              orden de compra o autorizacion formal,
                              regresa al paso anterior y selecciona
                              En ejecucion.
                            </span>
                          </div>
                        )}
                      </>
                    )}

                    {projectForm.situacionProyecto ===
                      "EJECUCION" && (
                      <>
                        <label className="pcc-project-field">
                          <span>Fase operativa *</span>

                          <select
                            value={projectForm.etapa}
                            onChange={(event) =>
                              setProjectForm((current) => ({
                                ...current,
                                etapa: event.target.value,
                              }))
                            }
                          >
                            {FASES.filter(
                              (fase) =>
                                fase !== "Idea" &&
                                fase !== "Cerrado"
                            ).map((fase) => (
                              <option
                                key={fase}
                                value={fase}
                              >
                                {fase}
                              </option>
                            ))}
                          </select>
                        </label>

                        <label className="pcc-project-field">
                          <span>Monto contratado *</span>

                          <small className="pcc-wizard-field-help">
                            Importe formal del contrato,
                            adjudicacion u orden de compra.
                          </small>

                          <div className="pcc-project-money-field">

                            <div className="pcc-project-money-input-wrap">

                              <span className="pcc-project-money-prefix">
                                $
                              </span>

                              <input
                                type="text"
                                inputMode="decimal"
                                autoComplete="off"
                                value={projectForm.montoContratado}
                                onChange={(event) => {
                                  const normalized =
                                    normalizeProjectMoneyInput(
                                      event.target.value
                                    );

                                  setProjectForm((current) => ({
                                    ...current,
                                    montoContratado:
                                      normalized,
                                  }));
                                }}
                                placeholder="20000000"
                                aria-label="Monto contratado en pesos"
                              />

                              <span className="pcc-project-money-currency">
                                MXN
                              </span>

                            </div>

                            <div className="pcc-project-money-confirmation">

                              <span>
                                Monto interpretado
                              </span>

                              <strong>
                                {formatProjectMoneyExact(
                                  projectForm.montoContratado
                                )}
                              </strong>

                              <em>
                                {formatProjectMoneyMdp(
                                  projectForm.montoContratado
                                )}
                              </em>

                            </div>

                          </div>
                        </label>
                      </>
                    )}

                    {isTerminalProjectSituation(
                      projectForm.situacionProyecto
                    ) && (
                      <>
                        <div className="pcc-wizard-calculation">
                          <span>Resultado</span>

                          <strong>
                            {getProjectSituationLabel(
                              projectForm.situacionProyecto
                            )}
                          </strong>

                          <small>
                            Determinado por la situación seleccionada.
                          </small>
                        </div>

                        <label className="pcc-project-field">
                          <span>Fecha de cierre *</span>

                          <input
                            type="date"
                            value={projectForm.fechaCierre}
                            onChange={(event) =>
                              setProjectForm((current) => ({
                                ...current,
                                fechaCierre:
                                  event.target.value,
                              }))
                            }
                          />
                        </label>

                        <label className="pcc-project-field pcc-project-field-full">
                          <span>
                            {normalizeProjectSituation(
                              projectForm.situacionProyecto
                            ) === PROJECT_SITUATIONS.NO_REALIZADA
                              ? "Motivo de no realización *"
                              : normalizeProjectSituation(
                                  projectForm.situacionProyecto
                                ) === PROJECT_SITUATIONS.MUERTO
                                ? "Motivo de cancelación *"
                                : "Resumen de cierre *"}
                          </span>

                          <textarea
                            rows="3"
                            value={projectForm.motivoCierre}
                            onChange={(event) =>
                              setProjectForm((current) => ({
                                ...current,
                                motivoCierre:
                                  event.target.value,
                              }))
                            }
                            placeholder="Describe brevemente el resultado o motivo"
                          />
                        </label>
                      </>
                    )}
{!isTerminalProjectSituation(
                      projectForm.situacionProyecto
                    ) && (<label className="pcc-project-field pcc-project-field-full">
                      <span>
                        {"Siguiente acci\u00F3n *"}
                      </span>

                      <small className="pcc-wizard-field-help">
                        Escribe exactamente que debe hacerse despues.
                      </small>

                      <textarea
                        rows="4"
                        value={projectForm.proximoPaso}
                        onChange={(event) =>
                          setProjectForm((current) => ({
                            ...current,
                            proximoPaso: event.target.value,
                          }))
                        }
                        placeholder="Ej. Enviar propuesta economica al cliente"
                      />
                    </label>                    )}

                  </div>

                  {!getProjectWizardStepState(
                    projectForm,
                    3
                  ).valid && (
                    <div className="pcc-wizard-inline-status">
                      Completa:{" "}
                      {getProjectWizardStepState(
                        projectForm,
                        3
                      ).missing.join(", ")}
                    </div>
                  )}
                </section>
              )}

              {projectWizardStep === 4 && (
                <section className="pcc-wizard-panel">
                  <div className="pcc-wizard-step-hero">
                    <span>PASO 4 DE 4</span>

                    <h3>
                      {"Confirma la informaci\u00F3n"}
                    </h3>

                    <p>
                      Revisa los datos principales antes de
                      crear definitivamente el proyecto.
                    </p>
                  </div>

                  <div className="pcc-wizard-review">

                    <div className="pcc-wizard-review-primary">
                      <span>Proyecto</span>

                      <strong>
                        {projectForm.nombre || "Sin nombre"}
                      </strong>

                      <small>
                        {projectForm.cliente ||
                          "Sin cliente registrado"}
                      </small>
                    </div>

                    <dl className="pcc-wizard-review-list">

                      <div>
                        <dt>Responsable</dt>
                        <dd>
                          {projectForm.responsable ||
                            "Sin responsable"}
                        </dd>
                      </div>

                      <div>
                        <dt>Situacion</dt>
                        <dd>
                          {getProjectSituationLabel(
                            projectForm.situacionProyecto
                          )}
                        </dd>
                      </div>

                      <div>
                        <dt>Prioridad</dt>
                        <dd>
                          {projectForm.prioridad || "MEDIA"}
                        </dd>
                      </div>

                      <div>
                        <dt>Fecha compromiso</dt>
                        <dd>
                          {projectForm.fechaCompromiso ||
                            "Sin compromiso"}
                        </dd>
                      </div>

                      {isCommercialProjectSituation(
                        projectForm.situacionProyecto
                      ) && (
                        <>
                          <div>
                            <dt>Etapa comercial</dt>
                            <dd>
                              {projectForm.etapaComercial}
                            </dd>
                          </div>

                          <div>
                            <dt>Valor estimado</dt>
                            <dd>
                              {formatProjectMoney(
                                projectForm.montoOportunidad
                              )}
                            </dd>
                          </div>

                          <div>
                            <dt>Probabilidad</dt>
                            <dd>
                              {getCommercialProbability(projectForm.etapaComercial, commercialStages)}
                              %
                            </dd>
                          </div>

                          <div>
                            <dt>Pipeline ponderado</dt>
                            <dd>
                              {formatProjectMoney(
                                getWeightedPipeline(
                                  projectForm.montoOportunidad,
                                  getCommercialProbability(projectForm.etapaComercial, commercialStages)
                                )
                              )}
                            </dd>
                          </div>
                        </>
                      )}

                      {projectForm.situacionProyecto ===
                        "EJECUCION" && (
                        <>
                          <div>
                            <dt>Fase operativa</dt>
                            <dd>
                              {projectForm.etapa}
                            </dd>
                          </div>

                          <div>
                            <dt>Monto contratado</dt>
                            <dd>
                              {formatProjectMoney(
                                projectForm.montoContratado
                              )}
                            </dd>
                          </div>
                        </>
                      )}

                      {isTerminalProjectSituation(
                        projectForm.situacionProyecto
                      ) && (
                        <>
                          <div>
                            <dt>Resultado</dt>

                            <dd>
                              {getProjectSituationLabel(
                                projectForm.situacionProyecto
                              )}
                            </dd>
                          </div>

                          <div>
                            <dt>Fecha de cierre</dt>

                            <dd>
                              {projectForm.fechaCierre ||
                                "Sin fecha"}
                            </dd>
                          </div>

                          <div>
                            <dt>Motivo / resumen</dt>

                            <dd>
                              {projectForm.motivoCierre ||
                                "Sin detalle"}
                            </dd>
                          </div>
                        </>
                      )}

                    </dl>

                    <div className="pcc-wizard-review-action">
                      <span>
                        {"Siguiente acci\u00F3n"}
                      </span>

                      <strong>
                        {projectForm.proximoPaso ||
                          "Sin siguiente accion"}
                      </strong>
                    </div>

                    <div
                      className={
                        "pcc-wizard-final-status " +
                        (
                          getProjectFormValidation(
                            projectForm
                          ).valid
                            ? "ready"
                            : "pending"
                        )
                      }
                    >
                      <strong>
                        {getProjectFormValidation(
                          projectForm
                        ).valid
                          ? "\u2713 Informacion completa"
                          : "Falta informacion obligatoria"}
                      </strong>

                      {!getProjectFormValidation(
                        projectForm
                      ).valid && (
                        <span>
                          Completa:{" "}
                          {getProjectFormValidation(
                            projectForm
                          ).missing.join(", ")}
                        </span>
                      )}
                    </div>

                  </div>

                  <div className="pcc-wizard-system-note">
                    Al crear el proyecto se utilizara el mismo
                    flujo actual de registro. Los pasos anteriores
                    no guardan informacion por separado.
                  </div>

                  {projectCreateError && (
                    <div
                      className="pcc-project-form-error"
                      role="alert"
                    >
                      {projectCreateError}
                    </div>
                  )}
                </section>
              )}

            </div>

            <div className="pcc-project-modal-actions pcc-wizard-actions">

              {projectWizardStep === 1 ? (
                <button
                  type="button"
                  className="pcc-project-cancel-btn"
                  onClick={cerrarModalNuevoProyecto}
                  disabled={projectCreateLoading}
                >
                  Cancelar
                </button>
              ) : (
                <button
                  type="button"
                  className="pcc-project-cancel-btn"
                  onClick={() => {
                    setProjectCreateError("");
                    setProjectWizardStep((current) =>
                      Math.max(1, current - 1)
                    );
                  }}
                  disabled={projectCreateLoading}
                >
                  {"\u2190 Regresar"}
                </button>
              )}

              {projectWizardStep < 4 ? (
                <button
                  type="button"
                  className="pcc-project-create-btn"
                  disabled={
                    !getProjectWizardStepState(
                      projectForm,
                      projectWizardStep
                    ).valid
                  }
                  onClick={() => {
                    const stepState =
                      getProjectWizardStepState(
                        projectForm,
                        projectWizardStep
                      );

                    if (!stepState.valid) {
                      return;
                    }

                    setProjectCreateError("");
                    setProjectWizardStep((current) =>
                      Math.min(4, current + 1)
                    );
                  }}
                >
                  {"Continuar \u2192"}
                </button>
              ) : (
                <button
                  type="submit"
                  className="pcc-project-create-btn"
                  disabled={
                    projectCreateLoading ||
                    !getProjectFormValidation(
                      projectForm
                    ).valid
                  }
                >
                  {projectCreateLoading
                    ? "Guardando proyecto..."
                    : "Crear proyecto"}
                </button>
              )}

            </div>
            </form>
        </div>
      )}
    </div>
  );
}


































